import {
  menu,
  popFilterableSimpleMenu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { cssVarV2 } from '@blocksuite/affine-shared/theme';
import {
  type InsertToPosition,
  streamLainAnimationController,
} from '@blocksuite/affine-shared/utils';
import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  DeleteIcon,
  ExpandFullIcon,
  LinkedPageIcon,
  MoveLeftIcon,
  MoveRightIcon,
  PlusIcon,
  ViewIcon,
} from '@blocksuite/icons/lit';
import { css } from '@emotion/css';
import { computed, effect, signal } from '@preact/signals-core';
import { html, nothing, type TemplateResult } from 'lit';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';

import {
  CanvasViewportParticipantController,
  type CanvasViewportRowAnchorRect,
  type CanvasViewportTargetBinding,
  type CanvasViewportTargetPosition,
  createRowAnchoredCanvasViewportTarget,
} from '../../../core/canvas-viewport.js';
import { popPropertiesSetting } from '../../../core/common/properties.js';
import {
  inputConfig,
  streamlainColumnMenuConfigs,
  typeConfig,
} from '../../../core/common/property-menu.js';
import { renderColumnHeader } from '../../../core/component/column-header/column-header.js';
import { groupTraitKey } from '../../../core/group-by/trait.js';
import {
  createUniComponentFromWebComponent,
  dataViewI18nT,
  getDataViewRuntimeLocale,
  renderUniLit,
} from '../../../core/index.js';
import {
  computePostDropShifts,
  LivePreviewReorderController,
  type ReorderGeometry,
  type ReorderHost,
  type RowReorderGhost,
} from '../../../core/row-reorder/index.js';
import {
  getShiftKeyActive$,
  RowSelectionController,
  type RowSelectionHost,
} from '../../../core/row-selection/index.js';
import { dragHandler } from '../../../core/utils/wc-dnd/dnd-context.js';
import { defaultActivators } from '../../../core/utils/wc-dnd/sensors/index.js';
import {
  createSortContext,
  sortable,
} from '../../../core/utils/wc-dnd/sort/sort-context.js';
import { horizontalListSortingStrategy } from '../../../core/utils/wc-dnd/sort/strategies/index.js';
import { linearMove } from '../../../core/utils/wc-dnd/utils/linear-move.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
  dispatchDatabaseLiveViewSetupStateChange,
} from '../../../core/view/data-view-base.js';
import {
  type DataViewBodyState,
  dataViewBodyStateValue,
  getDataViewBodyElement,
  measureDataViewBodyIntrinsicHeight,
} from '../../../core/view/data-view-body.js';
import type { Row } from '../../../core/view-manager/row.js';
import {
  type DataViewHorizontalScrollbar,
  getHorizontalWheelDelta,
} from '../../shared/horizontal-scrollbar.js';
import {
  getSetupWizardBodyIntrinsicHeight,
  renderSetupWizardBodyContent,
} from '../../shared/setup-wizard/setup-wizard-body.js';
import { setupWizardBodyHostStyle } from '../../shared/setup-wizard/setup-wizard-css.js';
import { slimDataViewScrollbarStyle } from '../../shared/slim-scrollbar-style.js';
import { DataViewColumnPreview } from '../../table/pc/header/column-renderer.js';
import { getVerticalIndicator } from '../../table/pc/header/vertical-indicator.js';
import {
  computeGanttAxisWindow,
  type GanttAxisWindow,
} from '../core/axis-window.js';
import { ensureDefaultGanttRowRange } from '../core/default-row-range.js';
import { clearBarStateCells } from '../core/position-pipeline.js';
import { MS_PER_DAY, TimeAxisMath } from '../core/time-axis-math.js';
import { VirtualScroller } from '../core/virtual-scroll/index.js';
import type { GanttZoomLevel } from '../define.js';
import type { BarState } from '../extensions/types.js';
import type {
  GanttColumnRole,
  GanttSingleView,
} from '../gantt-view-manager.js';
import type { GanttViewSelectionWithType } from '../selection.js';
import { ROW_HEIGHT } from './bars/bar-renderer.js';
import { readCellText } from './cell-display.js';
import type { LineEndpoints } from './dependencies/dependency-line.js';
import type { GanttLinkPort } from './dependencies/link-port.js';
import { detectMountMode, type GanttMountMode } from './edgeless-gantt-view.js';
import {
  ganttDocModeStyle,
  ganttEdgelessModeStyle,
} from './edgeless-gantt-view-css.js';
import {
  GANTT_SETUP_WIZARD_LAYOUT_CHANGE_EVENT,
  type GanttWizardStrings,
} from './setup-wizard/gantt-setup-wizard.js';
import { TIME_AXIS_HEADER_HEIGHT } from './time-axis/time-axis-css.js';

type EditableDataViewCellContainer = HTMLElement & {
  cell?: {
    afterEnterEditingMode?: () => void;
    beforeEnterEditMode?: () => boolean;
  };
};

/**
 * UI logic for the Gantt view (PC). The renderer in this bead is a
 * placeholder shell — the time axis, swimlanes and bars are added by
 * sl-p9sb.3.1, .3.2 and .3.3. Empty-state copy and the wizard trigger
 * land in sl-p9sb.2.1 / .2.2.
 */
export class GanttViewUILogic extends DataViewUILogicBase<
  GanttSingleView,
  GanttViewSelectionWithType
> {
  ui$ = signal<GanttViewUI | undefined>();

  clearSelection = (): void => {
    this.setSelection(undefined);
  };

  addRow = (position: InsertToPosition): string | undefined => {
    if (this.view.readonly$.value) return undefined;
    const rowId = this.view.rowAdd(position);
    ensureDefaultGanttRowRange(this.view, rowId);
    this.ui$.value?.requestUpdate();
    this.ui$.value?.queueIntrinsicContentHeightChange();
    return rowId;
  };

  override isSetupWizardOpen(): boolean {
    return this.ui$.value?.isSetupWizardOpen() ?? false;
  }

  focusFirstCell = (): void => {
    // No focusable cell yet — sl-p9sb.3.x adds the table-style left pane.
  };

  showIndicator = (_evt: MouseEvent): boolean => false;

  hideIndicator = (): void => {};

  moveTo = (_id: string, _evt: MouseEvent): void => {};

  renderer = createUniComponentFromWebComponent(GanttViewUI);
}

const GANTT_CANVAS_MAIN_SCROLL_TARGET = 'gantt-main';
const GANTT_CANVAS_LABEL_SCROLL_TARGET = 'gantt-label';

type GanttLabelLayoutState = 'closed' | 'title-only' | 'full';

type GanttVisibleTimelineRange = {
  endMs: number;
  startMs: number;
};

export class GanttViewUI extends DataViewUIBase<GanttViewUILogic> {
  private mountMode: GanttMountMode = 'doc';
  private readonly scrollContainerRef: Ref<HTMLDivElement> = createRef();

  private readonly horizontalScrollbarRef: Ref<DataViewHorizontalScrollbar> =
    createRef();
  private readonly wizardOpen = signal(false);
  private readonly pendingLinkEndpoints = signal<LineEndpoints | undefined>(
    undefined
  );

  /**
   * Shared drag-preview map keyed by rowId. Owned at the host level so
   * both the bar renderer (writes on pointermove) and the dependency
   * overlay (reads to place arrow endpoints) see the same source. This
   * is what makes dependency arrows glide with the cursor in real time
   * instead of snapping to the committed position on pointerup.
   */
  private readonly barPreviews = signal<ReadonlyMap<string, BarState>>(
    new Map()
  );

  /**
   * Row currently magnetized during a link-drag (see bar renderer's
   * `resolveMagnet`). Owned here rather than in the bar renderer so
   * the host can also toggle the `data-linking` class and so the
   * signal outlives the short-lived drag sessions.
   */
  private readonly magnetRowId = signal<string | null>(null);
  private autoOpenSeen = false;
  private canvasViewportRestored = false;
  private initialViewportApplied = false;
  private initialViewportQueued = false;
  private scroller: VirtualScroller | null = null;
  private readonly canvasViewport = new CanvasViewportParticipantController(
    this,
    'gantt',
    () => this.isSharedCanvasViewport
  );

  /**
   * Floating drag preview that follows the cursor during a row
   * reorder. Mounted under `document.body` for the same reason the
   * drag tooltip is — Edgeless wraps the scene in a `transform`, so
   * `position: fixed` children must escape the transformed ancestor
   * to resolve against the viewport.
   */
  private ghostElement: RowReorderGhost | null = null;

  private readonly reorder = new LivePreviewReorderController();

  /**
   * Shared multi-row selection store. Plain click replaces the set
   * with the clicked row, Shift+Click toggles or range-selects, Ctrl/
   * Meta+Click toggles individually. Group-move reads the set when a
   * drag starts on a selected bar or label row.
   */
  private readonly selectionHost: RowSelectionHost = {
    getRowIdsInOrder: () => this.logic.view.getBoundedRowIdsWindow().rowIds,
  };

  readonly selection = new RowSelectionController(this.selectionHost);

  /**
   * Timestamp currently centred in the viewport. Updated on every
   * scroll tick so that a zoom-level switch can restore the same
   * moment at the new pixel-per-day ratio. Null until the user or the
   * initial-scroll handler has placed the viewport at least once.
   */
  private viewportCenterMs: number | null = null;
  private readonly visibleTimelineRange =
    signal<GanttVisibleTimelineRange | null>(null);
  private zoomEffectArmed = false;
  private scrollHandlerCleanup: (() => void) | null = null;

  private readonly axisProjection$ = computed(() => {
    const view = this.logic.view;
    return view.projectRows({
      requestedPropertyIds: [
        view.startDateCol$.value,
        view.endDateCol$.value,
      ].filter((propertyId): propertyId is string => Boolean(propertyId)),
      scope: 'filtered-result',
      viewId: view.id,
      viewMode: view.type,
    });
  });

  private readonly axisWindow$ = computed<GanttAxisWindow>(() => {
    const view = this.logic.view;
    const rows = this.axisProjection$.value.rows;
    return computeGanttAxisWindow({
      endCol: view.endDateCol$.value,
      now: Date.now(),
      readCellValue: (rowId, propertyId) =>
        view.cellGetOrCreate(rowId, propertyId).jsonValue$.value,
      rows,
      startCol: view.startDateCol$.value,
    });
  });

  /**
   * Count of bars sitting outside the current scroll viewport on each
   * horizontal side. Drives the small "jump to offscreen items"
   * indicators that float over the left/right edges of the gantt body.
   */
  private readonly offscreen = signal<{ left: number; right: number }>({
    left: 0,
    right: 0,
  });

  /**
   * Width of the sticky title column. Backed by the view's Y.Doc
   * data (`labelColumnWidth`) so resize persists across reloads and
   * syncs with the per-extra-column widths — matches Table's
   * column-width contract. Falls back to
   * {@link DEFAULT_LABEL_COLUMN_WIDTH} when the user has never
   * dragged it.
   */
  private readonly labelColumnWidth = computed<number>(
    () =>
      this.labelLayoutMotion$.value?.titleWidth ??
      this.persistedLabelColumnWidth.value
  );
  private readonly persistedLabelColumnWidth = computed<number>(
    () => this.logic.view.labelColumnWidth$.value ?? DEFAULT_LABEL_COLUMN_WIDTH
  );

  /**
   * Displayed title/section widths while the label section slides open or
   * closed. The persisted widths already hold the target; this transient
   * override steps the layout there along the shared motion curve.
   */
  private readonly labelLayoutMotion$ = signal<{
    sectionMaxWidth: number;
    titleWidth: number;
  } | null>(null);
  private labelResizeCleanup: (() => void) | null = null;

  /**
   * Optional cap on the label-section's visible width. Stored in
   * Y.Doc so "I dragged the section to 500 px" carries across
   * reloads and devices in sync with the extras' widths.
   * `undefined` ⇒ no cap (section grows with its content).
   */
  private readonly labelSectionMaxWidth = computed<number | null>(
    () =>
      this.labelLayoutMotion$.value?.sectionMaxWidth ??
      this.persistedLabelSectionMaxWidth.value
  );
  private readonly persistedLabelSectionMaxWidth = computed<number | null>(
    () => this.logic.view.labelSectionMaxWidth$.value ?? null
  );

  // Horizontal scroll position of the sticky label section. A single
  // signal drives both the header and the body inner wrappers via
  // `transform: translateX(-X)`, so the two are always in sync by
  // construction — no ref/scroll-event plumbing, no ping-pong guards.
  // Wheel events on the sticky section consume horizontal delta into
  // this signal and `preventDefault` so the outer gantt scroll
  // container never sees them (no overscroll leak into the timeline).
  private readonly labelScrollLeft$ = signal(0);
  // Upper bound for `labelScrollLeft$`; updated at render time once the
  // natural content width and the clamped visible width are known.
  private labelMaxScroll = 0;

  private readonly columnSortItems$ = computed(() => {
    const titleWidth = this.labelColumnWidth.value;
    const labelLayoutState = this.getLabelLayoutState(titleWidth);
    if (labelLayoutState !== 'full') return [];
    const view = this.logic.view;
    const titleColumnId = view.mainProperties$.value.titleColumn;
    return view.properties$.value
      .filter(property => property.id !== titleColumnId)
      .map(property => property.id);
  });

  readonly columnSortContext = createSortContext({
    activators: defaultActivators,
    collisionDetection: linearMove(true),
    container: this,
    items: this.columnSortItems$,
    modifiers: [({ transform }) => ({ ...transform, y: 0 })],
    onDragEnd: ({ active, over }) => {
      if (this.logic.view.readonly$.value) return;
      if (!over || over.id === active.id) return;
      const items = this.columnSortItems$.value;
      const activeIndex = items.findIndex(id => id === active.id);
      const overIndex = items.findIndex(id => id === over.id);
      if (activeIndex < 0 || overIndex < 0) return;
      this.logic.view.propertyGetOrCreate(active.id).move({
        before: activeIndex > overIndex,
        id: over.id,
      });
    },
    createOverlay: active => {
      const column = this.logic.view.propertyGetOrCreate(active.id);
      const preview = new DataViewColumnPreview();
      preview.property = column;
      preview.rows = this.logic.view.getBoundedRowIdsWindow().rowIds;
      preview.container = this;
      preview.style.position = 'absolute';
      preview.style.zIndex = '999';
      preview.style.width = `${
        column.width$.value ?? LABEL_EXTRA_COLUMN_WIDTH
      }px`;
      const scale = this.columnSortContext.scale$.value;
      const offsetParentRect = this.offsetParent?.getBoundingClientRect();
      if (!offsetParentRect) return;
      preview.style.top = `${
        (active.rect.top - offsetParentRect.top - 1) / scale.y
      }px`;
      preview.style.left = `${
        (active.rect.left - offsetParentRect.left) / scale.x
      }px`;
      const cells = Array.from(
        this.querySelectorAll(`[data-gantt-extra-column-id="${active.id}"]`)
      ) as HTMLElement[];
      cells.forEach(ele => {
        ele.style.opacity = '0.1';
      });
      this.append(preview);
      return {
        overlay: preview,
        cleanup: () => {
          preview.remove();
          cells.forEach(ele => {
            ele.style.opacity = '1';
          });
        },
      };
    },
    strategy: horizontalListSortingStrategy,
  });

  /**
   * Observed width of the outer scroll container. A ResizeObserver
   * updates this signal so the render path can cap the sticky label
   * section to a viewport-dependent maximum even when the user has
   * not dragged the section handle. Without this, a small viewport
   * (or a narrow edgeless frame) would let the natural label width
   * exceed the viewport, parking the timeline entirely off-screen
   * with no way for the user to scroll past the sticky column.
   */
  private readonly viewportWidth$ = signal(0);
  private viewportResizeCleanup: (() => void) | null = null;

  /**
   * Exposed to the bar renderer so it can highlight selected bars and
   * drive group-move when the drag seed is part of the selection.
   */
  readonly selectedRowIds = this.selection.selectedIds$;

  /**
   * Tracks which surface the current selection came from. Drives the
   * Backspace semantics: bars → unschedule (clear dates), label rows →
   * delete the whole row (Table/Kanban parity). Reset to null on every
   * selection clear so an empty selection can't accidentally inherit
   * a stale source.
   */
  private readonly selectionSource$ = signal<'bar' | 'label' | null>(null);

  /**
   * Row ID whose title cell is currently in inline-edit mode. Set by
   * `dblclick` on the label text; cleared by pointerdown on anything
   * outside the active title cell. The title cell renderer watches a
   * derived `isEditing$` signal and toggles its rich-text editor's
   * `readonly` attribute off while this holds the row's id — matching
   * Table's click-to-edit behaviour on its title column.
   */
  private readonly editingCell$ = signal<{
    rowId: string;
    colId: string;
  } | null>(null);

  readonly selectRowFromBar = (rowId: string, shiftKey: boolean): void => {
    this.selection.select(rowId, { shift: shiftKey });
    this.selectionSource$.value = 'bar';
  };

  readonly selectRowFromLabel = (
    rowId: string,
    mods: { shift?: boolean; ctrl?: boolean; meta?: boolean } = {}
  ): void => {
    this.selection.select(rowId, mods);
    this.selectionSource$.value = 'label';
  };

  private readonly clearSelection = (evt: PointerEvent): void => {
    if (evt.shiftKey) return;
    const target = evt.target as HTMLElement | null;
    if (!target) return;
    // Pointerdown outside the currently-editing row exits title
    // inline-edit. Same gate as selection clear below — anything
    // outside bars, labels and the resizer counts as "elsewhere".
    const editing = this.editingCell$.value;
    if (editing) {
      const editingRow = target.closest(
        '[data-label-row-id]'
      ) as HTMLElement | null;
      if (editingRow?.dataset['labelRowId'] !== editing.rowId) {
        this.editingCell$.value = null;
      }
    }
    if (
      target.closest('[data-bar-row-id]') ||
      target.closest('[data-label-row-id]') ||
      target.closest(`.${labelColumnResizerStyle}`) ||
      target.closest(`.${extraColumnResizerStyle}`) ||
      target.closest(`.${sectionResizerStyle}`)
    ) {
      return;
    }
    this.selection.clear();
    this.selectionSource$.value = null;
  };

  /**
   * Backspace / Delete semantics depend on where the selection came
   * from:
   *   - bar selection   → unschedule (clear start/end cells).
   *                       The row record survives.
   *   - label selection → drop the whole row (Table/Kanban parity —
   *                       you delete a row by selecting it as a row).
   *
   * Having one hotkey with two behaviours keyed off the selection
   * source avoids stacking modifiers ("Shift+Backspace for rows,
   * plain Backspace for bars") which users forget and gets us
   * consistent muscle memory with Table/Kanban where Backspace on a
   * label-selected row always means "delete row".
   *
   * The listener sits on `window` so the hotkey works from anywhere
   * inside the gantt viewport but guards against hijacking the key
   * from real text inputs and content-editable blocks.
   */
  private readonly handleKeyDown = (evt: KeyboardEvent): void => {
    if (evt.key !== 'Backspace' && evt.key !== 'Delete') return;
    if (this.logic.view.readonly$.value) return;
    if (!this.contains(evt.target as Node | null)) return;
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable)
    ) {
      return;
    }
    const ids = [...this.selectedRowIds.value];
    if (ids.length === 0) return;
    evt.preventDefault();
    if (this.selectionSource$.value === 'label') {
      this.logic.view.rowsDelete(ids);
      this.selection.clear();
      this.selectionSource$.value = null;
    } else {
      clearBarStateCells(this.logic.view, ids);
    }
  };

  /**
   * Upstream hook for the relation service. When undefined, the bar
   * renderer falls back to an in-cell writer (see DefaultLinkPort).
   */
  linkPort: GanttLinkPort | undefined = undefined;

  private readonly setLinkPreview = (endpoints: LineEndpoints | null): void => {
    this.pendingLinkEndpoints.value = endpoints ?? undefined;
    // Mirror the drag-active state onto the DOM so CSS can reveal
    // every arrival handle on visible bars (see linkArrivalStyle's
    // `[data-linking] &` rule). Cleared on pointerup/cancel via the
    // same code path that clears the preview endpoint.
    if (endpoints) this.dataset.linking = 'true';
    else delete this.dataset.linking;
  };

  private readonly onWizardLayoutChange = (): void => {
    this.queueIntrinsicContentHeightChange();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    this.logic.ui$.value = this;
    this.mountMode = detectMountMode(this);
    this.classList.add('gantt-view', ganttViewStyle);
    this.classList.add(
      this.mountMode === 'edgeless' ? ganttEdgelessModeStyle : ganttDocModeStyle
    );
    this.dataset.ganttMode = this.mountMode;
    this.style.userSelect = 'none';
    this.style.display = 'flex';
    this.style.flexDirection = 'column';
    this.showColumnMoveIndicator();
    this.disposables.addFromEvent(
      this,
      GANTT_SETUP_WIZARD_LAYOUT_CHANGE_EVENT,
      this.onWizardLayoutChange
    );

    // Auto-open the wizard once when the view has no date columns
    // configured — subsequent openings are user-driven (settings panel).
    this.disposables.add(
      effect(() => {
        if (
          !this.logic.view.isConfigured$.value &&
          !this.autoOpenSeen &&
          !this.logic.view.readonly$.value
        ) {
          this.autoOpenSeen = true;
          this.setWizardOpen(true);
        }
      })
    );

    // Shift-key mirror for the cursor feedback on bars/rows. Reads the
    // shared window-level tracker so all views observe the same flag.
    this.disposables.add(
      effect(() => {
        const active = getShiftKeyActive$().value;
        if (active) this.dataset.shiftActive = 'true';
        else delete this.dataset.shiftActive;
      })
    );

    window.addEventListener('keydown', this.handleKeyDown);

    const ghost = document.createElement(
      'dv-row-reorder-ghost'
    ) as RowReorderGhost;
    document.body.appendChild(ghost);
    this.ghostElement = ghost;

    // Mirror the shared reorder state onto the floating ghost. The
    // view supplies the label strings; the ghost itself handles
    // stacking and overflow truncation.
    this.disposables.add(
      effect(() => {
        const state = this.reorder.state$.value;
        const pos = this.reorder.ghostPosition$.value;
        if (!state || !pos) {
          ghost.hide();
          return;
        }
        const labels = [...state.draggedIds].map(
          id => this.readRowTitle(id) || dataViewI18nT('gantt.bar.untitled')
        );
        ghost.show({
          clientX: pos.clientX,
          clientY: pos.clientY,
          labels,
        });
      })
    );

    // Refresh the offscreen counts whenever rows, zoom or label width
    // change — not just on scroll. Without this, adding/moving a bar
    // (or resizing the label column) could leave the indicator stale
    // because the scroll listener only fires on scroll events.
    this.disposables.add(
      effect(() => {
        // Explicit reads so the effect re-runs when any of these
        // signals change.
        void this.logic.view.rowCount$.value;
        void this.logic.view.zoomLevel$.value;
        void this.labelColumnWidth.value;
        const container = this.scrollContainerRef.value;
        if (!container) return;
        this.recomputeCurrentOffscreenCounts(container);
      })
    );

    // Preserve the currently-centred moment across zoom-level switches.
    // We subscribe to zoomLevel$ here (long-lived); the first run
    // merely arms the recenterer, every subsequent run schedules a
    // post-layout scroll to the stored `viewportCenterMs`.
    this.disposables.add(
      effect(() => {
        // Explicit read so the effect re-runs on change.
        const z = this.logic.view.zoomLevel$.value;
        if (this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
          void z;
          return;
        }
        if (!this.zoomEffectArmed) {
          this.zoomEffectArmed = true;
          return;
        }
        void z;
        const centerMs = this.viewportCenterMs;
        if (centerMs == null) return;
        // Wait one rAF so the axis content width has been rebuilt
        // under the new pxPerDay before we read clientWidth/scrollTo.
        requestAnimationFrame(() => this.recenterOn(centerMs));
      })
    );
  }

  override disconnectedCallback(): void {
    this.stopLabelLayoutMotion();
    this.scroller?.dispose();
    this.scroller = null;
    this.labelResizeCleanup?.();
    this.labelResizeCleanup = null;
    this.scrollHandlerCleanup?.();
    this.scrollHandlerCleanup = null;
    this.viewportResizeCleanup?.();
    this.viewportResizeCleanup = null;
    this.canvasViewport.dispose();
    window.removeEventListener('keydown', this.handleKeyDown);
    this.reorder.cancel();
    this.ghostElement?.remove();
    this.ghostElement = null;
    super.disconnectedCallback();
  }

  private readonly handleLabelResizePointerDown = (evt: PointerEvent): void => {
    evt.preventDefault();
    evt.stopPropagation();
    this.stopLabelLayoutMotion();
    const handle = evt.currentTarget as HTMLElement;
    const startX = evt.clientX;
    const startW = this.labelColumnWidth.value;
    handle.setPointerCapture(evt.pointerId);

    const onMove = (move: PointerEvent): void => {
      const next = Math.min(
        MAX_LABEL_COLUMN_WIDTH,
        Math.max(MIN_LABEL_COLUMN_WIDTH, startW + (move.clientX - startX))
      );
      this.logic.view.setLabelColumnWidth(next);
    };
    const onUp = (): void => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      try {
        handle.releasePointerCapture(evt.pointerId);
      } catch {
        // Pointer already released (capture lost on cancel) — ignore.
      }
      this.labelResizeCleanup = null;
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
    this.labelResizeCleanup = onUp;
  };

  /**
   * Drag-to-resize for an extra label column. Matches the title-column
   * handler but writes the width into view data (persisted) via
   * `GanttColumn.widthSet` — each move is a Y.Doc update, consistent
   * with Table's column-resize behaviour.
   */
  private readonly handleExtraColumnResize = (
    evt: PointerEvent,
    colId: string
  ): void => {
    evt.preventDefault();
    evt.stopPropagation();
    const handle = evt.currentTarget as HTMLElement;
    const property = this.logic.view.propertyGetOrCreate(colId);
    const startX = evt.clientX;
    const startW = property.width$.value ?? LABEL_EXTRA_COLUMN_WIDTH;
    handle.setPointerCapture(evt.pointerId);

    const onMove = (move: PointerEvent): void => {
      const next = Math.min(
        MAX_LABEL_EXTRA_COLUMN_WIDTH,
        Math.max(MIN_LABEL_EXTRA_COLUMN_WIDTH, startW + (move.clientX - startX))
      );
      property.widthSet(next);
    };
    bindResizePointerDrag(handle, evt.pointerId, onMove);
  };

  private showColumnMoveIndicator(): void {
    const columnMoveIndicator = getVerticalIndicator();
    this.disposables.add(
      effect(() => {
        const active = this.columnSortContext.active$.value;
        const over = this.columnSortContext.over$.value;
        if (!active || !over) {
          columnMoveIndicator.remove();
          return;
        }
        const scrollX = this.columnSortContext.scrollOffset$.value.x;
        const bottom =
          this.scrollContainerRef.value?.getBoundingClientRect().bottom ??
          this.getBoundingClientRect().bottom;
        const left =
          over.rect.left < active.rect.left ? over.rect.left : over.rect.right;
        const height = bottom - over.rect.top;
        columnMoveIndicator.display(left - scrollX, over.rect.top, height);
      })
    );
  }

  /**
   * Drag-resize the label section as a whole — distinct from the
   * per-column resize. The section's effective width is clamped to
   * `labelSectionMaxWidth`; the overflow is scrolled horizontally
   * inside the sticky label area. Persist-free (session-only signal)
   * to match the title-column resize behaviour.
   */
  private readonly handleSectionResizePointerDown = (
    evt: PointerEvent
  ): void => {
    evt.preventDefault();
    evt.stopPropagation();
    this.stopLabelLayoutMotion();
    const handle = evt.currentTarget as HTMLElement;
    const startX = evt.clientX;
    const currentMax = this.labelSectionMaxWidth.value;
    const totalAtStart =
      currentMax ??
      (() => {
        const titleWidth = this.labelColumnWidth.value;
        const view = this.logic.view;
        const titleColId = view.mainProperties$.value.titleColumn;
        const extras = view.properties$.value.filter(p => p.id !== titleColId);
        return extras.reduce(
          (sum, p) => sum + (p.width$.value ?? LABEL_EXTRA_COLUMN_WIDTH),
          titleWidth
        );
      })();
    handle.setPointerCapture(evt.pointerId);

    const onMove = (move: PointerEvent): void => {
      const next = Math.max(
        MIN_LABEL_SECTION_WIDTH,
        totalAtStart + (move.clientX - startX)
      );
      this.logic.view.setLabelSectionMaxWidth(next);
    };
    bindResizePointerDrag(handle, evt.pointerId, onMove);
  };

  /** Double-click the section handle to reset the max back to auto. */
  private readonly resetSectionWidth = (evt: Event): void => {
    evt.preventDefault();
    evt.stopPropagation();
    this.stopLabelLayoutMotion();
    this.logic.view.setLabelSectionMaxWidth(undefined);
  };

  /**
   * Consumes horizontal wheel delta inside the sticky label section.
   * Shift+wheel and trackpad horizontal gestures both land here.
   * Always `preventDefault`s when there is any label scroll range —
   * even at the edges — so horizontal intent never leaks out to the
   * outer gantt scroll container (which would otherwise scroll the
   * timeline). Vertical intent (pure deltaY without shift) bubbles
   * untouched so row-scroll still works.
   */
  /**
   * The scroll container hides its native horizontal bar (see
   * `dv-horizontal-scrollbar`), so horizontal wheel and trackpad gestures
   * are applied by hand. Label cells consume their own horizontal wheel
   * before it bubbles here.
   */
  private readonly handleTimelineWheel = (evt: WheelEvent): void => {
    const container = this.scrollContainerRef.value;
    if (!container) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) return;
    const delta = getHorizontalWheelDelta(evt, container.clientWidth);
    if (delta == null) return;
    evt.preventDefault();
    evt.stopPropagation();
    container.scrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, container.scrollLeft + delta)
    );
  };

  private readonly handleLabelWheel = (evt: WheelEvent): void => {
    const rawDx = evt.deltaX;
    const rawDy = evt.deltaY;
    // Treat shift+wheel as horizontal intent (normalize to deltaX).
    const deltaX =
      evt.shiftKey && Math.abs(rawDy) > Math.abs(rawDx) ? rawDy : rawDx;
    const deltaY = evt.shiftKey ? 0 : rawDy;
    const horizontal = Math.abs(deltaX) > Math.abs(deltaY);
    if (!horizontal) return;
    if (this.labelMaxScroll <= 0) return;
    const current = this.labelScrollLeft$.value;
    const next = Math.max(0, Math.min(this.labelMaxScroll, current + deltaX));
    // Consume the horizontal wheel regardless of whether it changed
    // anything — at the boundaries (next === current) we still have
    // to stop it from bubbling, otherwise the outer container scrolls
    // the timeline, which is exactly the overscroll the user hit.
    evt.preventDefault();
    evt.stopPropagation();
    if (next !== current) {
      this.labelScrollLeft$.value = next;
      this.canvasViewport.notifyTargetScroll(GANTT_CANVAS_LABEL_SCROLL_TARGET);
    }
  };

  applyCanvasViewportTarget(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): boolean {
    this.canvasViewportRestored = true;
    return this.canvasViewport.applyTarget(targetId, position);
  }

  /**
   * A canvas gantt without a stored shared viewport would open at the
   * axis origin (one year back), showing an empty timeline. Land on
   * "today" instead — through the participant controller so the default
   * is not echoed back and persisted as a user scroll. A stored canvas
   * viewport always wins, whether it arrives before or after this.
   */
  private applyDefaultCanvasViewport(): void {
    if (this.canvasViewportRestored) return;
    const container = this.scrollContainerRef.value;
    if (!container) return;
    this.canvasViewport.applyTarget(GANTT_CANVAS_MAIN_SCROLL_TARGET, {
      scrollLeft: this.todayScrollLeft(container),
      scrollTop: container.scrollTop,
    });
  }

  private todayScrollLeft(container: HTMLElement): number {
    return this.scrollLeftCentredOn(container, Date.now());
  }

  /**
   * Scroll position that centres `centerMs` in the visible bars area.
   * The sticky label section covers the left part of the container, so
   * centring on the full container width can hide the date behind it.
   */
  private scrollLeftCentredOn(container: HTMLElement, centerMs: number) {
    const { originMs } = this.getAxisWindow();
    // Read the current zoom afresh — the signal may have changed
    // again between rAF scheduling and firing.
    const math = new TimeAxisMath(this.logic.view.zoomLevel$.value, originMs);
    const barsVisibleWidth = Math.max(
      1,
      container.clientWidth -
        this.getLabelLayoutMetrics(this.logic.view, container.clientWidth)
          .totalLabelWidth
    );
    return Math.max(0, math.dateToX(centerMs) - barsVisibleWidth / 2);
  }

  getCanvasViewportTargets(): Record<string, CanvasViewportTargetPosition> {
    return this.canvasViewport.getTargets();
  }

  private getGanttRowAnchorRects(): CanvasViewportRowAnchorRect[] {
    const rows = this.logic.view.getBoundedRowIdsWindow().rowIds;
    return rows.map((rowId, index) => ({
      height: ROW_HEIGHT,
      rowId,
      top: TIME_AXIS_HEADER_HEIGHT + index * ROW_HEIGHT,
    }));
  }

  private get isSharedCanvasViewport(): boolean {
    return this.logic.view.featureFlags$.value.use_shared_canvas_viewport;
  }

  private createGanttLabelCanvasViewportTarget():
    | CanvasViewportTargetBinding
    | undefined {
    if (!this.isSharedCanvasViewport) {
      return undefined;
    }
    return {
      apply: position => {
        const next = Math.max(
          0,
          Math.min(this.labelMaxScroll, position.scrollLeft)
        );
        if (Math.abs(this.labelScrollLeft$.value - next) <= 2) {
          return false;
        }
        this.labelScrollLeft$.value = next;
        return true;
      },
      read: () => ({
        scrollLeft: this.labelScrollLeft$.value,
        scrollTop: 0,
      }),
      targetId: GANTT_CANVAS_LABEL_SCROLL_TARGET,
    };
  }

  private syncCanvasViewportParticipants(): void {
    this.canvasViewport.sync([
      createRowAnchoredCanvasViewportTarget(
        GANTT_CANVAS_MAIN_SCROLL_TARGET,
        this.scrollContainerRef.value,
        'both',
        () => this.getGanttRowAnchorRects()
      ),
      this.createGanttLabelCanvasViewportTarget(),
    ]);
  }

  private getLabelLayoutState(
    titleWidth = this.labelColumnWidth.value,
    sectionMaxWidth = this.labelSectionMaxWidth.value
  ): GanttLabelLayoutState {
    if (titleWidth <= MIN_LABEL_COLUMN_WIDTH + 4) return 'closed';
    if (sectionMaxWidth != null && sectionMaxWidth <= titleWidth + 4) {
      return 'title-only';
    }
    return 'full';
  }

  private getLabelLayoutMetrics(
    view: GanttSingleView = this.logic.view,
    viewport = this.viewportWidth$.value
  ) {
    const titleWidth = this.labelColumnWidth.value;
    const state = this.getLabelLayoutState(titleWidth);
    const showExtraLabelColumns = state === 'full';
    const titleColumnId = view.mainProperties$.value.titleColumn;
    const extraProperties = showExtraLabelColumns
      ? view.properties$.value.filter(property => property.id !== titleColumnId)
      : [];
    const extraCols = extraProperties.map(property => property.id);
    const extraWidthOf = (colId: string): number =>
      view.propertyGetOrCreate(colId).width$.value ?? LABEL_EXTRA_COLUMN_WIDTH;
    const extrasWidth = extraCols.reduce(
      (sum, id) => sum + extraWidthOf(id),
      0
    );
    const contentLabelWidth = titleWidth + extrasWidth;
    const sectionMaxWidth = this.labelSectionMaxWidth.value;
    const viewportCap =
      viewport > 0
        ? Math.max(MIN_LABEL_SECTION_WIDTH, viewport - GANTT_MIN_VISIBLE_PX)
        : Number.POSITIVE_INFINITY;
    const totalLabelWidth = Math.min(
      contentLabelWidth,
      sectionMaxWidth ?? Number.POSITIVE_INFINITY,
      viewportCap
    );
    return {
      contentLabelWidth,
      extraCols,
      extraWidthOf,
      state,
      titleWidth,
      totalLabelWidth,
    };
  }

  private readonly toggleLabelColumnCollapse = (evt: Event): void => {
    evt.preventDefault();
    evt.stopPropagation();
    // The next state follows the persisted target, so a click during the
    // motion continues the cycle instead of repeating the running step.
    const state = this.getLabelLayoutState(
      this.persistedLabelColumnWidth.value,
      this.persistedLabelSectionMaxWidth.value
    );
    const from = this.getLabelLayoutMetrics();
    if (state === 'full') {
      this.logic.view.setLabelColumnWidth(DEFAULT_LABEL_COLUMN_WIDTH);
      this.logic.view.setLabelSectionMaxWidth(DEFAULT_LABEL_COLUMN_WIDTH);
    } else if (state === 'title-only') {
      this.logic.view.setLabelColumnWidth(MIN_LABEL_COLUMN_WIDTH);
      this.logic.view.setLabelSectionMaxWidth(undefined);
    } else {
      this.logic.view.setLabelColumnWidth(DEFAULT_LABEL_COLUMN_WIDTH);
      this.logic.view.setLabelSectionMaxWidth(undefined);
    }
    this.labelScrollLeft$.value = 0;
    this.animateLabelLayout(from.titleWidth, from.totalLabelWidth);
  };

  /** Slide the label section from the displayed widths to the persisted ones. */
  private animateLabelLayout(fromTitle: number, fromSection: number): void {
    this.labelLayoutMotion$.value = null;
    const { titleWidth: toTitle, totalLabelWidth: toSection } =
      this.getLabelLayoutMetrics();
    const lerp = (from: number, to: number, progress: number) =>
      from + (to - from) * progress;
    // Hold the start widths until the first motion frame.
    this.labelLayoutMotion$.value = {
      sectionMaxWidth: fromSection,
      titleWidth: fromTitle,
    };
    const motion = streamLainAnimationController.animateProgress(
      this,
      progress => {
        this.labelLayoutMotion$.value =
          progress >= 1
            ? null
            : {
                sectionMaxWidth: lerp(fromSection, toSection, progress),
                titleWidth: lerp(fromTitle, toTitle, progress),
              };
      }
    );
    void motion.finished.then(outcome => {
      if (outcome === 'finished') this.labelLayoutMotion$.value = null;
    });
  }

  private stopLabelLayoutMotion(): void {
    streamLainAnimationController.cancel(this);
    this.labelLayoutMotion$.value = null;
  }

  /**
   * On first layout, restore the user's last-seen viewport centre (if
   * any) so switching views or reloading the page feels like coming back
   * to where they were. Falls back to "today" when no stored position
   * exists so a brand-new gantt still lands on a useful default.
   *
   * The timeline is often not scrollable at `firstUpdated` (rows or the
   * axis window still loading), and the browser clamps any scroll target
   * to 0 then — the gantt would stay at the axis origin a year back. So
   * this retries from `updated()` until the timeline overflows its
   * container and applies exactly once.
   */
  private queueInitialViewport(): void {
    if (this.initialViewportApplied || this.initialViewportQueued) return;
    if (!this.scrollContainerRef.value) return;
    this.initialViewportQueued = true;
    requestAnimationFrame(() => {
      this.initialViewportQueued = false;
      const container = this.scrollContainerRef.value;
      if (!container || container.scrollWidth <= container.clientWidth) {
        return;
      }
      this.initialViewportApplied = true;
      if (this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
        this.applyDefaultCanvasViewport();
        return;
      }
      const stored = this.readStoredScrollCenter();
      if (stored != null) this.recenterOn(stored);
      else this.scrollToToday('auto');
    });
  }

  protected override updated(): void {
    this.initVirtualScroll();
    this.installScrollTracker();
    this.installViewportObserver();
    this.syncDataViewBodyResizeObserver({
      body: this.getBodyElement(),
    });
    this.syncCanvasViewportParticipants();
    this.queueIntrinsicContentHeightChange();
    this.queueInitialViewport();
  }

  override getIntrinsicContentHeight(visualScale = 1): number {
    const scale = visualScale || 1;
    const body = this.getBodyElement();
    const intrinsicHeightGuard =
      this.mountMode === 'edgeless' ? EDGELESS_INTRINSIC_HEIGHT_GUARD : 0;
    const setupWizard = this.querySelector<HTMLElement>(
      'dv-gantt-setup-wizard'
    );
    const wizardHeight = getSetupWizardBodyIntrinsicHeight({
      host: setupWizard,
      visualScale: scale,
    });

    if (this.wizardOpen.value) {
      return wizardHeight + intrinsicHeightGuard;
    }

    if (!this.logic.view.isConfigured$.value) {
      return (
        Math.max(
          320,
          measureDataViewBodyIntrinsicHeight({ body, visualScale: scale }),
          wizardHeight
        ) + intrinsicHeightGuard
      );
    }

    // The horizontal scrollbar is an in-flow sibling of the scroll
    // container, which is the body's intrinsic element. Omitting it would
    // make an autosizing canvas block 16px too short and clip the footer.
    const horizontalScrollbarHeight =
      this.horizontalScrollbarRef.value?.offsetHeight ?? 0;
    return (
      Math.max(
        measureDataViewBodyIntrinsicHeight({ body, visualScale: scale }),
        wizardHeight
      ) +
      horizontalScrollbarHeight +
      intrinsicHeightGuard
    );
  }

  private getBodyElement(): HTMLElement | undefined {
    return getDataViewBodyElement(this);
  }

  isSetupWizardOpen(): boolean {
    return this.wizardOpen.value;
  }

  private dispatchSetupWizardStateChange(): void {
    dispatchDatabaseLiveViewSetupStateChange(this, this.wizardOpen.value);
  }

  private setWizardOpen(open: boolean): void {
    if (this.wizardOpen.value === open) {
      return;
    }
    this.wizardOpen.value = open;
    this.dispatchSetupWizardStateChange();
    this.queueIntrinsicContentHeightChange();
    this.requestUpdate();
  }

  /**
   * Namespaced localStorage key for a per-view preference. View id
   * keeps each gantt independent; the `gantt.` prefix scopes us away
   * from other subsystems sharing the bucket. Private-mode Safari /
   * quota errors bubble as silent misses — none of these values are
   * load-bearing, the UI just resets to defaults.
   */
  private viewPrefKey(suffix: string): string {
    return `gantt.${suffix}:${this.logic.view.id}`;
  }

  private readViewPrefNumber(suffix: string): number | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.viewPrefKey(suffix));
      if (!raw) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  private writeViewPrefNumber(suffix: string, value: number): void {
    if (this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
      return;
    }
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.viewPrefKey(suffix), String(value));
    } catch {
      // Ignore quota / disabled-storage failures.
    }
  }

  private readStoredScrollCenter(): number | null {
    return this.readViewPrefNumber('scrollCenter');
  }

  private writeStoredScrollCenter(ms: number): void {
    this.writeViewPrefNumber('scrollCenter', ms);
  }

  /**
   * Tracks the outer scroll container's client width in
   * `viewportWidth$` so the render path can derive a viewport-aware
   * cap for the sticky label section. Using a ResizeObserver catches
   * window resize, pane resize in edgeless, and DPI changes without
   * needing a `window.resize` listener that doesn't fire on local
   * layout changes.
   */
  private installViewportObserver(): void {
    const container = this.scrollContainerRef.value;
    if (!container || this.viewportResizeCleanup) return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      this.viewportWidth$.value = entry.contentRect.width;
      requestAnimationFrame(() => {
        if (this.scrollContainerRef.value === container) {
          this.recomputeCurrentOffscreenCounts(container);
        }
      });
    });
    ro.observe(container);
    // Seed the signal immediately so the first render already has a
    // real width — otherwise the initial frame would clamp to 0.
    this.viewportWidth$.value = container.clientWidth;
    this.viewportResizeCleanup = () => ro.disconnect();
  }

  /**
   * Attaches a passive scroll listener that refreshes
   * `viewportCenterMs` on every frame. The listener is cheap — a
   * single math op per scroll event — and lets the zoom-switch
   * effect re-centre on the user's actual viewport without any
   * per-frame cost when the zoom is stable.
   */
  private installScrollTracker(): void {
    const container = this.scrollContainerRef.value;
    if (!container || this.scrollHandlerCleanup) return;
    // Throttle localStorage writes to a single trailing call per
    // ~500 ms of idle — scroll events can fire 60+ times per second
    // during momentum scroll, and we don't need every frame on disk.
    let persistTimer: ReturnType<typeof setTimeout> | null = null;
    const schedulePersist = (ms: number): void => {
      if (this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
        return;
      }
      if (persistTimer != null) clearTimeout(persistTimer);
      persistTimer = setTimeout(() => {
        persistTimer = null;
        this.writeStoredScrollCenter(ms);
      }, 500);
    };
    const update = (): void => {
      const view = this.logic.view;
      const { originMs } = this.getAxisWindow();
      const math = new TimeAxisMath(view.zoomLevel$.value, originMs);
      // Sticky label section occupies the left side of the viewport;
      // subtract its effective width (title + visible extras), not only
      // the title column, before computing bars-relative coordinates.
      const barsVisibleWidth = Math.max(
        1,
        container.clientWidth -
          this.getLabelLayoutMetrics(view, container.clientWidth)
            .totalLabelWidth
      );
      const centreX = container.scrollLeft + barsVisibleWidth / 2;
      const centerMs = math.xToDate(centreX);
      const fromOffset = math.dateToX(this.getAxisWindow().fromMs);
      this.viewportCenterMs = centerMs;
      this.visibleTimelineRange.value = {
        endMs: math.xToDate(
          container.scrollLeft + barsVisibleWidth + fromOffset
        ),
        startMs: math.xToDate(container.scrollLeft + fromOffset),
      };
      schedulePersist(centerMs);
      this.recomputeCurrentOffscreenCounts(container, math);
    };
    const onScroll = () => update();
    container.addEventListener('scroll', onScroll, { passive: true });
    update();
    this.scrollHandlerCleanup = () => {
      if (persistTimer != null) clearTimeout(persistTimer);
      container.removeEventListener('scroll', onScroll);
    };
  }

  /**
   * Count how many bars sit completely outside the visible bars-area on
   * each side. "Outside on the left" = the bar's end is before the
   * visible left edge (i.e. the whole bar scrolled off). "Outside on
   * the right" = the bar's start is past the visible right edge. Bars
   * that only partially cross the edge stay counted as "visible" — the
   * user can still see part of them.
   */
  private recomputeOffscreenCounts(
    container: HTMLElement,
    math: TimeAxisMath,
    rows: readonly Row[]
  ): void {
    const view = this.logic.view;
    const startCol = view.startDateCol$.value;
    const endCol = view.endDateCol$.value;
    if (!startCol || !endCol) {
      this.offscreen.value = { left: 0, right: 0 };
      return;
    }
    const labelWidth = this.getLabelLayoutMetrics(
      view,
      container.clientWidth
    ).totalLabelWidth;
    const visibleWidth = Math.max(1, container.clientWidth - labelWidth);
    const visibleLeftX = container.scrollLeft;
    const visibleRightX = visibleLeftX + visibleWidth;

    let left = 0;
    let right = 0;
    for (const row of rows) {
      const s = view.cellGetOrCreate(row.rowId, startCol).jsonValue$.value;
      const e = view.cellGetOrCreate(row.rowId, endCol).jsonValue$.value;
      if (typeof s !== 'number' || typeof e !== 'number') continue;
      const barStartX =
        math.dateToX(s) - math.dateToX(this.getAxisWindow().fromMs);
      const barEndX =
        math.dateToX(e) - math.dateToX(this.getAxisWindow().fromMs);
      if (barEndX < visibleLeftX) left += 1;
      else if (barStartX > visibleRightX) right += 1;
    }
    this.offscreen.value = { left, right };
  }

  private recomputeCurrentOffscreenCounts(
    container: HTMLElement,
    math?: TimeAxisMath
  ): void {
    const view = this.logic.view;
    const { originMs } = this.getAxisWindow();
    const offscreenMath =
      math ?? new TimeAxisMath(view.zoomLevel$.value, originMs);
    const offscreenRows = this.isSharedCanvasViewport
      ? (this.scroller?.frame$.value?.visibleRows ?? [])
      : view.getBoundedRowsWindow().rows;
    this.recomputeOffscreenCounts(container, offscreenMath, offscreenRows);
  }

  /**
   * Scroll so the nearest off-viewport bar on the given side is brought
   * into view. Picks the bar closest to the current viewport edge so
   * the motion feels deterministic (no overshoot into the far edge).
   */
  private readonly jumpToOffscreen = (side: 'left' | 'right'): void => {
    const container = this.scrollContainerRef.value;
    if (!container) return;
    const view = this.logic.view;
    const startCol = view.startDateCol$.value;
    const endCol = view.endDateCol$.value;
    if (!startCol || !endCol) return;
    const { originMs, fromMs } = this.getAxisWindow();
    const math = new TimeAxisMath(view.zoomLevel$.value, originMs);
    const labelWidth = this.getLabelLayoutMetrics(
      view,
      container.clientWidth
    ).totalLabelWidth;
    const visibleWidth = Math.max(1, container.clientWidth - labelWidth);
    const visibleLeftX = container.scrollLeft;
    const visibleRightX = visibleLeftX + visibleWidth;
    const fromOffset = math.dateToX(fromMs);

    let bestX: number | null = null;
    for (const row of view.getBoundedRowsWindow().rows) {
      const s = view.cellGetOrCreate(row.rowId, startCol).jsonValue$.value;
      const e = view.cellGetOrCreate(row.rowId, endCol).jsonValue$.value;
      if (typeof s !== 'number' || typeof e !== 'number') continue;
      const startX = math.dateToX(s) - fromOffset;
      const endX = math.dateToX(e) - fromOffset;
      // Left: pick the rightmost off-left bar (closest edge, smallest
      // scroll travel). Right: pick the leftmost off-right bar for the
      // same reason on the other side. Both collapse to "closer
      // than the current best, if any".
      const isOffscreen =
        side === 'left' ? endX < visibleLeftX : startX > visibleRightX;
      if (!isOffscreen) continue;
      const closer =
        bestX === null || (side === 'left' ? startX > bestX : startX < bestX);
      if (closer) bestX = startX;
    }
    if (bestX === null) return;
    // Leave a small margin inside the viewport so the bar doesn't kiss
    // the edge — tempo feels gentler than a hard snap.
    const EDGE_MARGIN_PX = 24;
    const target =
      side === 'left'
        ? Math.max(0, bestX - EDGE_MARGIN_PX)
        : bestX - visibleWidth + EDGE_MARGIN_PX * 2;
    // Shared motion curve, stepped per frame: native smooth scrolling races
    // the canvas viewport reconciliation, which could restore the old position
    // before the offscreen bar became reachable.
    streamLainAnimationController.scrollTo(container, {
      left: Math.max(0, target),
    });
  };

  private readonly recenterOn = (centerMs: number): void => {
    const container = this.scrollContainerRef.value;
    if (!container) return;
    container.scrollTo({
      left: this.scrollLeftCentredOn(container, centerMs),
      behavior: 'auto',
    });
  };

  /**
   * Wires the virtual scroller to the configured scroll container and
   * keeps the bar renderer on the emitted frame. Skipped until the
   * container ref is available (non-configured state renders without
   * a scroll host).
   */
  private initVirtualScroll(): void {
    if (this.scroller || !this.scrollContainerRef.value) return;
    const scroller = new VirtualScroller({
      view: this.logic.view,
      rowHeight: ROW_HEIGHT,
      getScrollContainer: () => this.scrollContainerRef.value ?? null,
      onFrame: () => {
        this.requestUpdate();
        this.queueIntrinsicContentHeightChange();
      },
    });
    scroller.init();
    this.scroller = scroller;
  }

  openWizard = (): void => {
    this.setWizardOpen(true);
  };

  private readonly closeWizard = (): void => {
    this.setWizardOpen(false);
  };

  private getBodyState(): DataViewBodyState {
    if (this.wizardOpen.value) {
      return 'setup';
    }
    return this.logic.view.isConfigured$.value ? 'data' : 'empty';
  }

  /**
   * Pulls wizard copy from the tenant-installed hook so the frontend
   * i18n layer owns every string. Returns `{}` on the default Lit
   * playground (no hook registered) — the wizard then falls back to
   * the English defaults shipped with {@link GanttSetupWizard}.
   */
  private getWizardStrings(): Partial<GanttWizardStrings> {
    const win = window as Window & {
      __streamlainHooks?: {
        ganttWizardStrings?: Partial<GanttWizardStrings>;
      };
    };
    return win.__streamlainHooks?.ganttWizardStrings ?? {};
  }

  override render(): TemplateResult {
    return html`
      <div
        class="${setupWizardBodyHostStyle} ${ganttBodyStyle}"
        data-data-view-body="true"
        data-data-view-body-state=${dataViewBodyStateValue(this.getBodyState())}
      >
        ${this.renderBodyContent()}
      </div>
    `;
  }

  private renderBodyContent(): TemplateResult {
    return renderSetupWizardBodyContent({
      configured: this.logic.view.isConfigured$.value,
      renderConfigured: () => html`
        ${this.renderMissingColumnsBanner()} ${this.renderConfigured()}
      `,
      renderEmptyState: () => this.renderEmptyState(),
      renderSetupWizard: () => this.renderSetupWizard(),
      wizardOpen: this.wizardOpen.value,
    });
  }

  private renderSetupWizard(): TemplateResult {
    return html`<dv-gantt-setup-wizard
      class=${ganttSetupWizardInlineStyle}
      .contained=${false}
      .inline=${true}
      .view=${this.logic.view}
      .onClose=${this.closeWizard}
      .strings=${this.getWizardStrings()}
    ></dv-gantt-setup-wizard>`;
  }

  /**
   * Reactive banner that surfaces when a column the gantt points to
   * (start, end, progress, …) has been deleted in the table view. Built
   * off `missingColumnRoles$` so it re-evaluates automatically on every
   * column add/remove — no polling. The CTA re-opens the setup wizard
   * pre-populated against the current schema so users can re-pick a
   * column or create a fresh one in the same flow as first-run setup.
   */
  private renderMissingColumnsBanner(): TemplateResult | typeof nothing {
    const missing = this.logic.view.missingColumnRoles$.value;
    if (missing.length === 0) return nothing;
    if (this.logic.view.readonly$.value) return nothing;
    const roleLabels: Record<GanttColumnRole, string> = {
      start: dataViewI18nT('gantt.missingColumns.start'),
      end: dataViewI18nT('gantt.missingColumns.end'),
      progress: dataViewI18nT('gantt.missingColumns.progress'),
      status: dataViewI18nT('gantt.missingColumns.status'),
      assignee: dataViewI18nT('gantt.missingColumns.assignee'),
      dependencies: dataViewI18nT('gantt.missingColumns.dependencies'),
    };
    const labels = missing.map(role => roleLabels[role]).join(', ');
    const message =
      missing.length === 1
        ? dataViewI18nT('gantt.missingColumns.single', { label: labels })
        : dataViewI18nT('gantt.missingColumns.multiple', {
            count: missing.length,
            labels,
          });
    return html`<div class=${ganttMissingColumnsBannerStyle}>
      <span class=${ganttMissingColumnsBannerTextStyle}>${message}</span>
      <button
        class=${ganttMissingColumnsBannerButtonStyle}
        type="button"
        @click=${this.openWizard}
      >
        Reconfigure
      </button>
    </div>`;
  }

  private getAxisWindow(): GanttAxisWindow {
    return this.axisWindow$.value;
  }

  private readonly scrollToToday = (
    behavior: ScrollBehavior = 'smooth'
  ): void => {
    const container = this.scrollContainerRef.value;
    if (!container) return;
    container.scrollTo({
      left: this.todayScrollLeft(container),
      behavior,
    });
  };

  private readonly scrollTimelineByVisibleRange = (direction: -1 | 1): void => {
    const container = this.scrollContainerRef.value;
    if (!container) return;
    const labelWidth = this.getLabelLayoutMetrics(
      this.logic.view,
      container.clientWidth
    ).totalLabelWidth;
    const visibleWidth = Math.max(240, container.clientWidth - labelWidth);
    container.scrollBy({
      behavior: 'smooth',
      left: direction * visibleWidth,
    });
  };

  private readonly scrollToPreviousRange = (): void => {
    this.scrollTimelineByVisibleRange(-1);
  };

  private readonly scrollToNextRange = (): void => {
    this.scrollTimelineByVisibleRange(1);
  };

  private getToolbarTimelineRange(
    fromMs: number,
    toMs: number
  ): GanttVisibleTimelineRange {
    const visibleRange = this.visibleTimelineRange.value;
    if (visibleRange) return visibleRange;

    const zoomDays: Record<GanttZoomLevel, number> = {
      day: 7,
      month: 90,
      quarter: 180,
      week: 28,
    };
    const zoom = this.logic.view.zoomLevel$.value;
    const centerMs = this.viewportCenterMs ?? Date.now();
    const halfRangeMs = (zoomDays[zoom] * MS_PER_DAY) / 2;
    return {
      endMs: Math.min(toMs, centerMs + halfRangeMs),
      startMs: Math.max(fromMs, centerMs - halfRangeMs),
    };
  }

  private formatGanttRangeTitle(range: GanttVisibleTimelineRange): string {
    const start = new Date(range.startMs);
    const end = new Date(Math.max(range.startMs, range.endMs - MS_PER_DAY));
    const zoom = this.logic.view.zoomLevel$.value;
    const options: Intl.DateTimeFormatOptions =
      zoom === 'month' || zoom === 'quarter'
        ? { month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'short', year: 'numeric' };
    const formatter = new Intl.DateTimeFormat(
      getDataViewRuntimeLocale(),
      options
    );

    return typeof formatter.formatRange === 'function'
      ? formatter.formatRange(start, end)
      : `${formatter.format(start)} - ${formatter.format(end)}`;
  }

  private readonly openRowDetail = (rowId: string): void => {
    this.logic.root.openDetailPanel({ view: this.logic.view, rowId });
  };

  /**
   * Label-row click mirrors Kanban/Table selection semantics:
   *   plain click        → replace selection with this row
   *   shift+click        → range select from anchor to this row
   *   cmd/ctrl+click     → toggle this row in/out of selection
   *
   * The detail panel no longer auto-opens on plain click — that path
   * now lives on the per-row peek button (see `renderLabelPeekButton`)
   * so users can multi-select rows without getting the panel in their
   * face. Users who want "click to open" can still double-click
   * through the peek button or use the keyboard shortcut.
   */
  private readonly handleLabelRowClick = (
    evt: MouseEvent,
    rowId: string
  ): void => {
    evt.preventDefault();
    evt.stopPropagation();
    this.selectRowFromLabel(rowId, {
      shift: evt.shiftKey,
      ctrl: evt.ctrlKey,
      meta: evt.metaKey,
    });
  };

  /**
   * Right-click on a label row opens a Table/Kanban-style row context
   * menu. Single-row selection gets the full "Expand / Insert / Delete"
   * set; multi-selection collapses to "Delete N Rows" (the only action
   * that makes sense across many rows). Matches the behaviour of
   * `tableView.pc.menu.popRowMenu` so users carry muscle memory
   * between views.
   */
  private readonly handleLabelContextMenu = (
    evt: MouseEvent,
    rowId: string
  ): void => {
    if (this.logic.view.readonly$.value) return;
    evt.preventDefault();
    evt.stopPropagation();
    const target = evt.currentTarget as HTMLElement | null;
    if (!target) return;
    this.openLabelRowMenu(target, rowId);
  };

  /**
   * Shared menu popper used by both right-click and the "…" row-op
   * button so a user who can't right-click (touch, demo screen-share)
   * still reaches the same actions as mouse users.
   */
  private openLabelRowMenu(target: HTMLElement, rowId: string): void {
    if (this.logic.view.readonly$.value) return;

    const current = this.selectedRowIds.value;
    const rightClickInSelection = current.has(rowId);
    if (!rightClickInSelection) {
      this.selectRowFromLabel(rowId, {});
    } else {
      this.selectionSource$.value = 'label';
    }
    const ids = rightClickInSelection ? [...current] : [rowId];

    if (ids.length > 1) {
      popFilterableSimpleMenu(popupTargetFromElement(target), [
        menu.action({
          name: dataViewI18nT('row.deleteCount', { count: ids.length }),
          prefix: DeleteIcon(),
          class: { 'delete-item': true },
          select: () => {
            this.confirmAndDeleteRows(ids);
          },
        }),
      ]);
      return;
    }

    popFilterableSimpleMenu(popupTargetFromElement(target), [
      menu.action({
        name: dataViewI18nT('row.expand'),
        prefix: ExpandFullIcon(),
        select: () => {
          this.openRowDetail(rowId);
        },
      }),
      menu.group({
        name: '',
        items: [
          menu.action({
            name: dataViewI18nT('row.insertBefore'),
            prefix: html`<div
              style="transform: rotate(90deg);display:flex;align-items:center;"
            >
              ${MoveLeftIcon()}
            </div>`,
            select: () => {
              this.logic.view.rowAdd({ before: true, id: rowId });
            },
          }),
          menu.action({
            name: dataViewI18nT('row.insertAfter'),
            prefix: html`<div
              style="transform: rotate(90deg);display:flex;align-items:center;"
            >
              ${MoveRightIcon()}
            </div>`,
            select: () => {
              this.logic.view.rowAdd({ before: false, id: rowId });
            },
          }),
        ],
      }),
      menu.group({
        name: '',
        items: [
          menu.action({
            name: dataViewI18nT('row.delete'),
            prefix: DeleteIcon(),
            class: { 'delete-item': true },
            select: () => {
              this.logic.view.rowsDelete([rowId]);
              this.selection.clear();
              this.selectionSource$.value = null;
            },
          }),
        ],
      }),
    ]);
  }

  /**
   * Multi-row delete goes through the tenant-installed confirm hook
   * (same window global the Table view uses) so the UX — including
   * the "Are you sure? N rows will be removed" modal — stays in
   * lockstep with Table/Kanban delete.
   */
  private readonly confirmAndDeleteRows = (ids: readonly string[]): void => {
    const win = window as Window & {
      __streamlainHooks?: {
        confirmDeleteRows?: (count: number) => Promise<boolean>;
      };
    };
    const confirm = win.__streamlainHooks?.confirmDeleteRows;
    const run = (): void => {
      this.logic.view.rowsDelete([...ids]);
      this.selection.clear();
      this.selectionSource$.value = null;
    };
    if (confirm && ids.length > 1) {
      void confirm(ids.length).then(ok => {
        if (ok) run();
      });
      return;
    }
    run();
  };

  /**
   * Drop indicator between rows during a reorder drag. `null` → no
   * active reorder. `targetIndex` is a gap position in `rows$`: 0 means
   * above the first row, `rows.length` means below the last.
   */
  private readonly openPropertiesMenu = (evt: MouseEvent): void => {
    evt.preventDefault();
    evt.stopPropagation();
    popPropertiesSetting(
      popupTargetFromElement(evt.currentTarget as HTMLElement),
      { view: this.logic.view }
    );
  };

  /**
   * Per-column menu for label-column extras — mirrors the table
   * header's rename / type / move / hide / delete list so the two
   * views feel interchangeable. The menu uses the property API
   * directly (same calls the table view makes), meaning renames and
   * reorders propagate through `property.nameSet` / `property.move`
   * and land in every view that observes the same property.
   */
  private readonly openExtraColumnMenu = (
    evt: MouseEvent,
    property: GanttSingleView['propertyGetOrCreate'] extends (
      id: string
    ) => infer P
      ? P
      : never
  ): void => {
    if (this.logic.view.readonly$.value) return;
    evt.preventDefault();
    evt.stopPropagation();
    popMenu(popupTargetFromElement(evt.currentTarget as HTMLElement), {
      options: {
        items: [
          inputConfig(property),
          typeConfig(property),
          ...streamlainColumnMenuConfigs(property),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.hideInView'),
                prefix: ArrowLeftSmallIcon(),
                hide: () => !property.hideCanSet,
                select: () => property.hideSet(true),
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.moveLeft'),
                prefix: MoveLeftIcon(),
                hide: () => property.isFirst$.value,
                select: () => {
                  const pre = property.prev$.value;
                  if (pre) property.move({ id: pre.id, before: true });
                },
              }),
              menu.action({
                name: dataViewI18nT('property.moveRight'),
                prefix: MoveRightIcon(),
                hide: () => property.isLast$.value,
                select: () => {
                  const next = property.next$.value;
                  if (next) property.move({ id: next.id, before: false });
                },
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: dataViewI18nT('property.delete'),
                prefix: DeleteIcon(),
                hide: () => !property.canDelete,
                select: (): void | false => {
                  const name = property.name$.value || 'this column';
                  if (
                    !confirm(
                      `Delete column "${name}"? All data in this column will be permanently lost.`
                    )
                  ) {
                    return false;
                  }
                  property.delete?.();
                },
                class: { 'delete-item': true },
              }),
            ],
          }),
        ],
      },
    });
  };

  /**
   * Shift + drag on the label column starts a lasso multi-select.
   * Rows whose y-band intersects the rubber band join the existing
   * selection. Uses a 3-px movement threshold so a shift-click-without-
   * drag still routes through the per-row click handler (range
   * select) — lasso only kicks in on actual drag intent.
   */
  private readonly handleLabelColumnPointerDown = (evt: PointerEvent): void => {
    if (evt.button !== 0 || !evt.shiftKey) return;
    if (this.logic.view.readonly$.value) return;
    const column = evt.currentTarget as HTMLElement | null;
    if (!column) return;

    const startX = evt.clientX;
    const startY = evt.clientY;
    const columnRect = column.getBoundingClientRect();
    const rows = this.getRowsWindowSnapshot();
    const anchor = startY - columnRect.top;
    const initial = new Set(this.selectedRowIds.value);
    let active = false;

    const hitTest = (moveY: number): void => {
      const current = moveY - columnRect.top;
      const top = Math.min(anchor, current);
      const bottom = Math.max(anchor, current);
      const next = new Set(initial);
      rows.forEach((row, idx) => {
        const rowTop = idx * ROW_HEIGHT;
        const rowBottom = rowTop + ROW_HEIGHT;
        if (rowBottom > top && rowTop < bottom) {
          next.add(row.rowId);
        }
      });
      this.selection.setSelected(next, null);
      this.selectionSource$.value = 'label';
    };

    const onMove = (move: PointerEvent): void => {
      if (!active) {
        const dx = move.clientX - startX;
        const dy = move.clientY - startY;
        if (Math.hypot(dx, dy) < 3) return;
        active = true;
      }
      hitTest(move.clientY);
    };
    const cleanup = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', cleanup);
      window.removeEventListener('pointercancel', cleanup);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
  };

  /**
   * Label-row drag entry point. Waits for a small vertical movement so
   * a plain click (which also hits `pointerdown`) doesn't accidentally
   * trip a reorder. Horizontal intent aborts — there is no horizontal
   * action on the label column.
   */
  private readonly handleLabelRowPointerDown = (
    evt: PointerEvent,
    rowId: string
  ): void => {
    if (evt.button !== 0 || evt.shiftKey) return;
    const target = evt.currentTarget as HTMLElement | null;
    const host = target?.parentElement ?? null;
    if (!target || !host) return;

    const startX = evt.clientX;
    const startY = evt.clientY;
    const onMove = (move: PointerEvent): void => {
      const dx = move.clientX - startX;
      const dy = move.clientY - startY;
      if (Math.hypot(dx, dy) < 3) return;
      cleanup();
      if (Math.abs(dx) > Math.abs(dy)) return;
      this.beginRowReorder(move, rowId, target, host);
    };
    const onUp = (): void => cleanup();
    const cleanup = (): void => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  /**
   * Bar-area entry point. The bar renderer has already decided the
   * drag is a reorder (vertical dominance / Alt held), so the
   * controller starts immediately. `captureTarget` is the bar element
   * so Edgeless pan doesn't steal the pointermove stream.
   */
  readonly startRowReorderFromBar = (
    evt: PointerEvent,
    rowId: string,
    captureTarget: HTMLElement
  ): void => {
    const host = captureTarget.closest<HTMLElement>(`.${ganttBarsAreaStyle}`);
    if (!host) return;
    this.beginRowReorder(evt, rowId, captureTarget, host);
  };

  private beginRowReorder(
    pointer: PointerEvent,
    seedRowId: string,
    captureTarget: HTMLElement,
    coordHost: HTMLElement
  ): void {
    if (this.logic.view.readonly$.value) return;
    this.reorder.start({
      seedRowId,
      selectedIds: this.selection.selectedIds$.value,
      pointer,
      captureTarget,
      geometry: this.buildReorderGeometry(coordHost),
      host: this.reorderHost,
    });
  }

  private buildReorderGeometry(coordHost: HTMLElement): ReorderGeometry {
    const rect = coordHost.getBoundingClientRect();
    return {
      pointToTargetIndex: (_clientX, clientY) => {
        const rows = this.getRowsWindowSnapshot();
        const yInHost = clientY - rect.top + (coordHost.scrollTop ?? 0);
        const rawIdx = Math.round(yInHost / ROW_HEIGHT);
        const index = Math.max(0, Math.min(rows.length, rawIdx));
        return { index };
      },
    };
  }

  /**
   * Reorder host. `moveRows` walks the dragged rows in their original
   * order and delegates each move to the Y.js-backed `row.move`. The
   * controller hands us an `InsertToPosition` whose anchor id (when
   * present) is guaranteed not to be in the dragged set.
   */
  private readonly reorderHost: ReorderHost = {
    getRowIdsInOrder: () => this.logic.view.getBoundedRowIdsWindow().rowIds,
    getRowTitles: ids =>
      ids.map(
        id => this.readRowTitle(id) || dataViewI18nT('gantt.bar.untitled')
      ),
    moveRows: (rowIds, target) => {
      const view = this.logic.view;
      const idSet = new Set(rowIds);
      const orderedDragged = view
        .getBoundedRowsWindow()
        .rows.filter(r => idSet.has(r.rowId))
        .map(r => r.rowId);
      for (const id of orderedDragged) {
        const row = view.getBoundedRowsWindow().rows.find(r => r.rowId === id);
        if (!row) continue;
        row.move(target);
      }
    },
  };

  private readonly createFirstRow = (): void => {
    this.activateInsertedTitleCell(this.logic.addRow('end'));
  };

  private readonly handleAddRowClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    this.activateInsertedTitleCell(this.logic.addRow('end'));
  };

  private readonly stopAddRowPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  private blurExternalActiveElement(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && !this.contains(active)) {
      active.blur();
    }
  }

  private activateInsertedTitleCell(rowId: string | undefined): void {
    const colId = this.logic.view.mainProperties$.value.titleColumn;
    if (!rowId || !colId) {
      return;
    }
    this.blurExternalActiveElement();
    this.editingCell$.value = { rowId, colId };
    this.requestUpdate();
    this.queueIntrinsicContentHeightChange();
    void this.updateComplete.then(() => {
      const editing = this.editingCell$.value;
      if (editing?.rowId !== rowId || editing.colId !== colId) {
        return;
      }
      const row = this.querySelector<HTMLElement>(
        `[data-label-row-id="${CSS.escape(rowId)}"]`
      );
      const cell = row?.querySelector<EditableDataViewCellContainer>(
        'dv-data-view-cell-container'
      );
      if (cell?.cell?.beforeEnterEditMode?.() === false) {
        this.editingCell$.value = null;
        return;
      }
      this.requestUpdate();
      void this.updateComplete.then(() => {
        const updatedEditing = this.editingCell$.value;
        if (updatedEditing?.rowId !== rowId || updatedEditing.colId !== colId) {
          return;
        }
        row
          ?.querySelector<EditableDataViewCellContainer>(
            'dv-data-view-cell-container'
          )
          ?.cell?.afterEnterEditingMode?.();
      });
    });
  }

  private renderConfigured(): TemplateResult {
    const { fromMs, toMs, originMs } = this.getAxisWindow();
    const view = this.logic.view;
    const groupTrait = view.traitGet(groupTraitKey);
    const groups = groupTrait?.property$.value
      ? (groupTrait.groupsDataListAll$.value ?? [])
      : null;
    const rows = groups
      ? groups.flatMap(group => (group.hide$.value ? [] : group.rows))
      : view.getBoundedRowsWindow().rows;
    const visibleRowIds = groups
      ? new Set(rows.map(row => row.rowId))
      : undefined;
    const showAddRow = !view.readonly$.value;
    const rowsHeight = rows.length * ROW_HEIGHT;
    const zoom = view.zoomLevel$.value;
    const axisWidth = new TimeAxisMath(zoom, originMs).dateToX(toMs);
    const rangeTitle = this.formatGanttRangeTitle(
      this.getToolbarTimelineRange(fromMs, toMs)
    );

    if (rows.length === 0) {
      return html`
        <dv-gantt-zoom-toolbar
          .view=${view}
          .onScrollToToday=${this.scrollToToday}
          .onScrollToPreviousRange=${this.scrollToPreviousRange}
          .onScrollToNextRange=${this.scrollToNextRange}
          .rangeTitle=${rangeTitle}
        ></dv-gantt-zoom-toolbar>
        <dv-gantt-empty-db
          data-data-view-body-content="true"
          data-data-view-body-intrinsic="true"
          data-data-view-body-render-content="true"
          data-data-view-body-render-root="true"
          .onCreate=${this.createFirstRow}
        ></dv-gantt-empty-db>
      `;
    }

    const frame = this.scroller?.frame$.value ?? null;
    const visibleRows = visibleRowIds
      ? (frame?.visibleRows ?? []).filter(row => visibleRowIds.has(row.rowId))
      : (frame?.visibleRows ?? []);
    const rowIndexOffset = frame?.viewport.rowRange.top ?? 0;

    const {
      contentLabelWidth,
      extraCols,
      extraWidthOf,
      state: labelLayoutState,
      titleWidth,
      totalLabelWidth,
    } = this.getLabelLayoutMetrics(view);
    const labelCollapsed = labelLayoutState === 'closed';
    const totalWidth = totalLabelWidth + axisWidth;
    const labelRows = visibleRows;
    const rowShifts = this.computeRowShifts();

    // Horizontal scroll inside the sticky label section. Clamp the
    // signal value in case the content just shrank (columns hidden,
    // section widened) so the translate offset never exceeds the
    // scrollable range.
    this.labelMaxScroll = Math.max(0, contentLabelWidth - totalLabelWidth);
    if (this.labelScrollLeft$.value > this.labelMaxScroll) {
      this.labelScrollLeft$.value = this.labelMaxScroll;
    }
    const labelScrollLeft = this.labelScrollLeft$.value;
    const labelInnerTransform = `translateX(${-labelScrollLeft}px)`;

    // Per-row leading icon mirrors the Table title cell: a rounded,
    // background-tinted box that shows the linked-doc icon when the
    // title cell references a doc, otherwise the `iconColumn` value
    // (typically the block-type icon). Hoisted out of the row.map()
    // so we only resolve the column IDs once per frame.
    const iconColumnId = view.mainProperties$.value.iconColumn;
    const titleColumnId = view.mainProperties$.value.titleColumn;

    return html`
      <dv-gantt-zoom-toolbar
        .view=${view}
        .onScrollToToday=${this.scrollToToday}
        .onScrollToPreviousRange=${this.scrollToPreviousRange}
        .onScrollToNextRange=${this.scrollToNextRange}
        .rangeTitle=${rangeTitle}
      ></dv-gantt-zoom-toolbar>
      ${groups
        ? html`<div class=${ganttGroupHeaderListStyle}>
            ${groups.map(group => {
              const collapsed = group.hide$.value;
              return html`<dv-gantt-swimlane-header
                .label=${group.name$.value}
                .rowCount=${group.totalCount}
                .collapsed=${collapsed}
                .onToggle=${() => {
                  group.hideSet(!collapsed);
                  this.requestUpdate();
                }}
              ></dv-gantt-swimlane-header>`;
            })}
          </div>`
        : nothing}
      <div
        class=${ganttScrollContainerStyle}
        data-data-view-body-content="true"
        data-data-view-body-intrinsic="true"
        data-data-view-body-render-root="true"
        ${ref(this.scrollContainerRef)}
        @pointerdown=${this.clearSelection}
        @wheel=${this.handleTimelineWheel}
      >
        <div
          class=${ganttAxisContentStyle}
          data-data-view-body-render-content="true"
          style="width: ${totalWidth}px; min-height: ${rowsHeight +
          TIME_AXIS_HEADER_HEIGHT}px;"
        >
          <div class=${ganttAxisHeaderRowStyle}>
            <div
              class=${ganttLabelHeaderCellStyle}
              style="width: ${totalLabelWidth}px; flex: 0 0 ${totalLabelWidth}px; position: sticky;"
              @wheel=${this.handleLabelWheel}
            >
              <div class=${ganttLabelHeaderContentStyle}>
                <div
                  class=${ganttLabelHeaderInnerStyle}
                  style="width: ${contentLabelWidth}px; transform: ${labelInnerTransform};"
                >
                  <div
                    class=${labelHeaderTitleCellStyle}
                    style="width: ${titleWidth}px; flex: 0 0 ${titleWidth}px;"
                    data-collapsed=${labelCollapsed ? 'true' : 'false'}
                  >
                    ${this.renderLabelHeaderContent()}
                    <div
                      class=${labelColumnResizerStyle}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize title column"
                      title="Drag to resize, double-click to collapse/expand"
                      @pointerdown=${this.handleLabelResizePointerDown}
                      @dblclick=${this.toggleLabelColumnCollapse}
                    ></div>
                  </div>
                  ${extraCols.map(colId => {
                    const property = view.propertyGetOrCreate(colId);
                    const name = property.name$.value ?? '';
                    const width = extraWidthOf(colId);
                    return html`<div
                      class=${labelHeaderExtraCellStyle}
                      style="width: ${width}px; flex: 0 0 ${width}px;"
                      title=${name}
                      ${dragHandler(colId)}
                      ${sortable(colId)}
                    >
                      ${renderColumnHeader(property, {
                        onClick: e => this.openExtraColumnMenu(e, property),
                        onContextMenu: e =>
                          this.openExtraColumnMenu(e, property),
                        readonly: view.readonly$.value,
                      })}
                      <div
                        class=${extraColumnResizerStyle}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize column"
                        @pointerdown=${(e: PointerEvent) =>
                          this.handleExtraColumnResize(e, colId)}
                      ></div>
                    </div>`;
                  })}
                </div>
              </div>
              ${this.renderLabelHeaderActions(labelLayoutState)}
              <div
                class=${sectionResizerStyle}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize label section"
                title="Drag to resize label section, double-click to reset"
                @pointerdown=${this.handleSectionResizePointerDown}
                @dblclick=${this.resetSectionWidth}
              ></div>
            </div>
            <dv-gantt-time-axis-header
              class=${ganttAxisHeaderFillStyle}
              .zoom=${zoom}
              .originMs=${originMs}
              .fromMs=${fromMs}
              .toMs=${toMs}
            ></dv-gantt-time-axis-header>
          </div>
          <div class=${ganttRowsRowStyle} style="height: ${rowsHeight}px;">
            <div
              class=${ganttLabelColumnStyle}
              style="width: ${totalLabelWidth}px; flex: 0 0 ${totalLabelWidth}px; position: sticky;"
              @pointerdown=${this.handleLabelColumnPointerDown}
              @wheel=${this.handleLabelWheel}
            >
              <div class=${ganttLabelColumnContentStyle}>
                <div
                  class=${ganttLabelColumnInnerStyle}
                  style="width: ${contentLabelWidth}px; transform: ${labelInnerTransform};"
                >
                  ${labelRows.map((row, i) => {
                    const index = rowIndexOffset + i;
                    const selected = this.selectedRowIds.value.has(row.rowId);
                    const reorderActive =
                      this.reorder.state$.value?.draggedIds.has(row.rowId) ??
                      false;
                    const shiftPx = rowShifts.get(row.rowId) ?? 0;
                    const titleText =
                      this.readRowTitle(row.rowId) ||
                      dataViewI18nT('gantt.bar.untitled');
                    const reorderBusy = rowShifts.size > 0;
                    return html`<div
                      class=${ganttLabelRowStyle}
                      data-label-row-id=${row.rowId}
                      ?data-selected=${selected}
                      ?data-dragging=${reorderActive}
                      style="top: ${index *
                      ROW_HEIGHT}px; height: ${ROW_HEIGHT}px; width: ${contentLabelWidth}px; transform: translateY(${shiftPx}px); transition: ${reorderBusy
                        ? 'transform 120ms ease-out'
                        : 'none'};"
                      @pointerdown=${(e: PointerEvent) =>
                        this.handleLabelRowPointerDown(e, row.rowId)}
                      @click=${(e: MouseEvent) =>
                        this.handleLabelRowClick(e, row.rowId)}
                      @contextmenu=${(e: MouseEvent) =>
                        this.handleLabelContextMenu(e, row.rowId)}
                    >
                      <div
                        class=${labelCellStyle}
                        style="width: ${titleWidth}px; flex: 0 0 ${titleWidth}px;"
                        title=${titleText}
                        data-collapsed=${labelCollapsed ? 'true' : 'false'}
                      >
                        <dv-row-drag-handle
                          class="show-on-hover-row ${labelDragHandleSlotStyle}"
                          .onSelect=${(mods: {
                            shift?: boolean;
                            ctrl?: boolean;
                            meta?: boolean;
                          }) => this.selectRowFromLabel(row.rowId, mods)}
                        ></dv-row-drag-handle>
                        <span
                          class=${labelCheckboxSlotStyle}
                          data-role="checkbox-slot"
                          @pointerdown=${(e: PointerEvent) =>
                            e.stopPropagation()}
                        >
                          <dv-row-select-checkbox
                            .selected=${selected}
                            .onToggle=${(mods: {
                              shift?: boolean;
                              ctrl?: boolean;
                              meta?: boolean;
                            }) => this.selectRowFromLabel(row.rowId, mods)}
                          ></dv-row-select-checkbox>
                        </span>
                        ${(() => {
                          if (titleColumnId) {
                            const titleValue = view.cellGetOrCreate(
                              row.rowId,
                              titleColumnId
                            ).value$.value as
                              | { deltas$?: { value?: unknown[] } }
                              | undefined;
                            const deltas = titleValue?.deltas$?.value;
                            const linked = Array.isArray(deltas)
                              ? deltas.some(
                                  d =>
                                    (
                                      d as {
                                        attributes?: {
                                          reference?: { pageId?: string };
                                        };
                                      }
                                    )?.attributes?.reference?.pageId
                                )
                              : false;
                            if (linked) {
                              return html`<span class=${labelTypeIconStyle}
                                >${LinkedPageIcon()}</span
                              >`;
                            }
                          }
                          if (!iconColumnId) return nothing;
                          const iconValue = view.cellGetOrCreate(
                            row.rowId,
                            iconColumnId
                          ).value$.value;
                          if (!iconValue) return nothing;
                          return html`<span class=${labelTypeIconStyle}
                            >${iconValue}</span
                          >`;
                        })()}
                        ${this.renderLabelCell(
                          row.rowId,
                          titleColumnId,
                          titleText
                        )}
                        ${view.readonly$.value
                          ? nothing
                          : html`<dv-row-ops
                              class="show-on-hover-row ${labelRowOpsSlotStyle}"
                              .onPeek=${() => this.openRowDetail(row.rowId)}
                              .onMore=${(target: HTMLElement) =>
                                this.openLabelRowMenu(target, row.rowId)}
                            ></dv-row-ops>`}
                      </div>
                      ${extraCols.map(colId => {
                        const text = readCellText(view, row.rowId, colId);
                        const width = extraWidthOf(colId);
                        return html`<div
                          class=${labelCellExtraStyle}
                          style="width: ${width}px; flex: 0 0 ${width}px;"
                          title=${text}
                          data-gantt-extra-column-id=${colId}
                        >
                          ${this.renderLabelCell(row.rowId, colId, text)}
                        </div>`;
                      })}
                    </div>`;
                  })}
                  ${this.renderReorderIndicator(contentLabelWidth)}
                </div>
              </div>
              <div
                class=${sectionResizerStyle}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize label section"
                title="Drag to resize label section, double-click to reset"
                @pointerdown=${this.handleSectionResizePointerDown}
                @dblclick=${this.resetSectionWidth}
              ></div>
            </div>
            <div class=${ganttBarsAreaStyle}>
              <div class=${ganttBarsRowSeparatorLayerStyle} aria-hidden="true">
                ${visibleRows.map((_row, i) => {
                  const rowIndex = rowIndexOffset + i;
                  return html`<div
                    class=${ganttBarsRowSeparatorStyle}
                    style="top: ${rowIndex * ROW_HEIGHT + ROW_HEIGHT - 1}px;"
                  ></div>`;
                })}
              </div>
              <dv-gantt-today-indicator
                .zoom=${zoom}
                .originMs=${originMs}
                .fromMs=${fromMs}
                .toMs=${toMs}
              ></dv-gantt-today-indicator>
              <dv-gantt-bar-renderer
                .view=${view}
                .zoom=${zoom}
                .originMs=${originMs}
                .fromMs=${fromMs}
                .visibleRows=${visibleRows}
                .rowIndexOffset=${rowIndexOffset}
                .onBarClick=${this.openRowDetail}
                .linkPort=${this.linkPort}
                .onLinkPreview=${this.setLinkPreview}
                .selectedRowIds=${this.selectedRowIds}
                .onSelectRow=${this.selectRowFromBar}
                .onRowReorderStart=${this.startRowReorderFromBar}
                .rowShifts=${rowShifts}
                .previewOverrides=${this.barPreviews}
                .magnetRowId=${this.magnetRowId}
              ></dv-gantt-bar-renderer>
              <dv-gantt-dependency-overlay
                .view=${view}
                .zoom=${zoom}
                .originMs=${originMs}
                .fromMs=${fromMs}
                .visibleRows=${visibleRows}
                .rowIndexOffset=${rowIndexOffset}
                .pendingEndpoints=${this.pendingLinkEndpoints.value}
                .barPreviews=${this.barPreviews}
              ></dv-gantt-dependency-overlay>
            </div>
          </div>
          ${showAddRow
            ? html`<div
                class=${ganttAddRowStyle}
                style="width: ${totalWidth}px;"
                role="button"
                data-test-id="affine-database-add-row-button"
                @pointerdown=${this.stopAddRowPointerDown}
                @click=${this.handleAddRowClick}
              >
                <span class=${ganttAddRowStickyButtonStyle}>
                  ${PlusIcon()}<span
                    >${dataViewI18nT('toolbar.newRecord')}</span
                  >
                </span>
              </div>`
            : nothing}
        </div>
      </div>
      <dv-horizontal-scrollbar
        ${ref(this.horizontalScrollbarRef)}
        .target=${this.scrollContainerRef.value}
        .viewportWidth=${this.viewportWidth$.value}
        .contentWidth=${totalWidth}
        .label=${'Horizontal timeline scroll'}
      ></dv-horizontal-scrollbar>
      ${this.renderOffscreenIndicator('left', totalLabelWidth)}
      ${this.renderOffscreenIndicator('right', 0)}
    `;
  }

  /**
   * Architectural invariant: the gantt body has two horizontal regions
   *
   *   [0 .. totalLabelWidth]            OWNED by the sticky label column
   *   [totalLabelWidth .. bodyWidth]    OWNED by the scrolling bars area
   *
   * Any floating affordance the host renders at `ganttBodyStyle` level
   * — offscreen indicators here, any future pinned overlay — MUST be
   * positioned at `totalLabelWidth + pad` (left side) or `pad` from the
   * right edge. Rendering in the 0..totalLabelWidth region is how the
   * indicator ended up painting over the sticky column in the first
   * place; the `offset` argument exists to make the invariant part of
   * the call-site signature rather than a commented-out rule.
   */
  private renderOffscreenIndicator(
    side: 'left' | 'right',
    offset: number
  ): TemplateResult | typeof nothing {
    const count =
      side === 'left' ? this.offscreen.value.left : this.offscreen.value.right;
    if (count === 0) return nothing;
    const container = this.scrollContainerRef.value;
    if (!container) return nothing;
    const arrow = side === 'left' ? '‹' : '›';
    const cls =
      side === 'left'
        ? offscreenIndicatorLeftStyle
        : offscreenIndicatorRightStyle;
    const edgeStyle = `${
      side === 'left' ? `left: ${offset + 6}px;` : `right: ${offset + 6}px;`
    } top: ${this.offscreenIndicatorTop(container)}px;`;
    return html`<button
      class=${cls}
      type="button"
      style=${edgeStyle}
      aria-label=${`Jump to ${count} item${count === 1 ? '' : 's'} on the ${side}`}
      title=${`${count} item${count === 1 ? '' : 's'} ${
        side === 'left' ? 'before' : 'after'
      } the visible range`}
      @click=${() => this.jumpToOffscreen(side)}
    >
      <span class=${offscreenIndicatorArrowStyle}>${arrow}</span>
      <span class=${offscreenIndicatorCountStyle}>${count}</span>
    </button>`;
  }

  /**
   * Vertical centre of the visible row body (below the sticky time axis,
   * above the add-row strip and any empty space), in the indicator's
   * containing block. Centring on the whole host would float the pill
   * into empty space whenever the rows are shorter than the surface.
   */
  private offscreenIndicatorTop(container: HTMLElement): number {
    const rowsHeight =
      this.logic.view.getBoundedRowsWindow().rows.length * ROW_HEIGHT;
    const visibleRowsBottom = Math.max(
      TIME_AXIS_HEADER_HEIGHT + 1,
      Math.min(
        container.clientHeight,
        TIME_AXIS_HEADER_HEIGHT + rowsHeight - container.scrollTop
      )
    );
    return (
      container.offsetTop + (TIME_AXIS_HEADER_HEIGHT + visibleRowsBottom) / 2
    );
  }

  private renderReorderIndicator(
    totalLabelWidth: number
  ): TemplateResult | typeof nothing {
    const state = this.reorder.state$.value;
    if (!state || state.targetIndex === null) return nothing;
    const y = state.targetIndex * ROW_HEIGHT;
    return html`<div
      class=${reorderIndicatorStyle}
      style="top: ${y}px; width: ${totalLabelWidth}px;"
    ></div>`;
  }

  /**
   * For every row, compute the vertical pixel offset that will make the
   * row appear at its post-drop position while the reorder is live.
   * Stationary rows either pass through unchanged or shift by one
   * row-height to open a slot for the dragged block; dragged rows
   * themselves translate straight to their future contiguous slot at
   * `targetIndex`.
   */
  private computeRowShifts(): Map<string, number> {
    const state = this.reorder.state$.value;
    if (!state) return new Map();
    const rows = this.getRowsWindowSnapshot();
    const items = rows.map(r => ({ rowId: r.rowId, height: ROW_HEIGHT }));
    return computePostDropShifts(items, state);
  }

  /**
   * Renders a label-row cell inline using the property's own renderer.
   * Click enters edit mode, outside-click / Escape exits —
   * matching Table's click-to-edit behaviour. `editingCell$` gates
   * which (row, column) pair is live; `isEditing$` is a derived signal
   * so the cell-renderer can toggle its `readonly` attribute without
   * reading the whole ui-logic's state.
   */
  private renderLabelCell(
    rowId: string,
    colId: string | undefined,
    fallbackText: string
  ): unknown {
    const view = this.logic.view;
    if (!colId) {
      return html`<span class=${ganttLabelTextStyle}>${fallbackText}</span>`;
    }
    const prop = view.propertyGetOrCreate(colId);
    if (!prop.renderer$.value) {
      return html`<span class=${ganttLabelTextStyle}>${fallbackText}</span>`;
    }
    const isEditing$ = computed(() => {
      const e = this.editingCell$.value;
      return e?.rowId === rowId && e.colId === colId;
    });
    const isActive = (): boolean => {
      const e = this.editingCell$.value;
      return e?.rowId === rowId && e.colId === colId;
    };
    const onSelectCurrentCell = (editing: boolean): void => {
      if (view.readonly$.value) return;
      this.editingCell$.value = editing ? { rowId, colId } : null;
    };
    // Delegates rendering, sizing, padding, and overflow handling
    // to the view-agnostic `<dv-data-view-cell-container>` so every
    // property (member, created-by, rich-text, ...) paints exactly
    // like it does in the table view. The gantt-specific behaviour
    // — click isolation in the label slot and row-selection ownership,
    // wrapping in the flex label slot — sits on this wrapper span.
    return html`<span
      class=${ganttLabelCellSlotStyle}
      @dblclick=${(e: MouseEvent) => {
        if (view.readonly$.value) return;
        e.stopPropagation();
        this.editingCell$.value = { rowId, colId };
      }}
      @click=${(e: MouseEvent) => {
        if (isActive() || e.target !== e.currentTarget) e.stopPropagation();
      }}
      @pointerdown=${(e: PointerEvent) => {
        if (isActive()) e.stopPropagation();
      }}
    >
      <dv-data-view-cell-container
        .property=${prop}
        .rowId=${rowId}
        .isEditing$=${isEditing$}
        .onSelectCurrentCell=${onSelectCurrentCell}
        .enterEditOnClick=${!view.readonly$.value}
      ></dv-data-view-cell-container>
    </span>`;
  }

  private getRowsWindowSnapshot(): Row[] {
    return this.logic.view.getBoundedRowsWindow().rows;
  }

  private renderLabelHeaderContent(): TemplateResult {
    const titleCol = this.logic.view.mainProperties$.value.titleColumn;
    const titleProp = titleCol
      ? this.logic.view.propertyGetOrCreate(titleCol)
      : undefined;
    const propertyName = titleProp?.name$.value ?? 'Tasks';
    const headerIcon = titleProp?.icon;
    const collapsed = this.getLabelLayoutState() === 'closed';
    return html`
      ${collapsed
        ? nothing
        : html`<span class=${labelHeaderLeadingSpacerStyle}></span>`}
      ${collapsed || !headerIcon
        ? nothing
        : html`<span class=${labelHeaderIconStyle}
            >${renderUniLit(headerIcon)}</span
          >`}
      ${collapsed
        ? nothing
        : html`<span class=${labelHeaderTextStyle}>${propertyName}</span>`}
    `;
  }

  private renderLabelHeaderActions(
    state: GanttLabelLayoutState
  ): TemplateResult {
    const collapsed = state === 'closed';
    const nextLabel =
      state === 'full'
        ? dataViewI18nT('gantt.label.showTitleOnly')
        : state === 'title-only'
          ? dataViewI18nT('gantt.label.collapseTitleColumn')
          : dataViewI18nT('gantt.label.showFullLabelColumns');
    const propertiesLabel = dataViewI18nT('gantt.label.showHideProperties');
    return html`<div class=${labelHeaderActionsStyle}>
      ${collapsed
        ? nothing
        : html`<button
            class=${labelCollapseButtonStyle}
            type="button"
            aria-label=${propertiesLabel}
            title=${propertiesLabel}
            @click=${this.openPropertiesMenu}
            @pointerdown=${(e: Event) => e.stopPropagation()}
          >
            ${ViewIcon()}
          </button>`}
      <button
        class=${labelCollapseButtonStyle}
        type="button"
        aria-label=${nextLabel}
        title=${nextLabel}
        @click=${this.toggleLabelColumnCollapse}
        @pointerdown=${(e: Event) => e.stopPropagation()}
      >
        ${collapsed ? ArrowRightSmallIcon() : ArrowLeftSmallIcon()}
      </button>
    </div>`;
  }

  private readRowTitle(rowId: string): string {
    const titleCol = this.logic.view.mainProperties$.value.titleColumn;
    if (!titleCol) return '';
    return (
      this.logic.view.cellGetOrCreate(rowId, titleCol).stringValue$.value ?? ''
    );
  }

  private renderEmptyState(): TemplateResult {
    return html`<div
      class="${ganttEmptyStyle}"
      data-data-view-body-content="true"
      data-data-view-body-intrinsic="true"
      data-data-view-body-render-content="true"
      data-data-view-body-render-root="true"
    >
      <div class="${ganttEmptyTitleStyle}">
        ${dataViewI18nT('gantt.empty.title')}
      </div>
      <div class="${ganttEmptyHintStyle}">
        ${dataViewI18nT('gantt.empty.description')}
      </div>
      <button class=${wizardOpenerStyle} @click=${this.openWizard}>
        ${dataViewI18nT('gantt.empty.configureColumns')}
      </button>
    </div>`;
  }
}

const ganttViewStyle = css({
  userSelect: 'none',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '320px',
  minWidth: 0,
});

const ganttBodyStyle = css({
  minHeight: 0,
});

const ganttSetupWizardInlineStyle = css({
  display: 'block',
  flex: '0 0 auto',
  width: '100%',
});

const ganttScrollContainerStyle = css({
  position: 'relative',
  flex: '1 1 auto',
  minWidth: 0,
  // The shared `dv-horizontal-scrollbar` stays visible under the rows even
  // with overlay scrollbars; the native horizontal bar would duplicate it.
  overflowX: 'hidden',
  overflowY: 'auto',
  ...slimDataViewScrollbarStyle,
});

const ganttGroupHeaderListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  borderTop: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
});

// Default / collapsed / max width of the sticky title column. Each
// visible non-title property contributes `LABEL_EXTRA_COLUMN_WIDTH`
// to the sticky section's width.
/**
 * Shared cleanup for the pointer-drag used by each column-resize /
 * section-resize handle. Factored out so the individual handlers only
 * carry their own `onMove` logic — the listen/release boilerplate is
 * identical across every resize handle and would otherwise duplicate
 * across three sites.
 */
function bindResizePointerDrag(
  handle: HTMLElement,
  pointerId: number,
  onMove: (move: PointerEvent) => void
): () => void {
  const onUp = (): void => {
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', onUp);
    handle.removeEventListener('pointercancel', onUp);
    try {
      handle.releasePointerCapture(pointerId);
    } catch {
      // Pointer already released (capture lost on cancel) — ignore.
    }
  };
  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
  handle.addEventListener('pointercancel', onUp);
  return onUp;
}

const DEFAULT_LABEL_COLUMN_WIDTH = 220;
const MIN_LABEL_COLUMN_WIDTH = 32;
const MAX_LABEL_COLUMN_WIDTH = 480;
const LABEL_EXTRA_COLUMN_WIDTH = 140;
const MIN_LABEL_EXTRA_COLUMN_WIDTH = 60;
const MAX_LABEL_EXTRA_COLUMN_WIDTH = 480;
const MIN_LABEL_SECTION_WIDTH = 160;

// Minimum timeline pane width preserved on any viewport size. The
// viewport-cap on the sticky label ensures `viewport - this` px stays
// reserved for the gantt itself — narrower viewports auto-clamp the
// label and enable its internal horizontal scroll instead of pushing
// the bars off-screen. 240 px fits ~3 week columns at default zoom.
const GANTT_MIN_VISIBLE_PX = 240;

// Height reserved below the last row for the inline "+ New Record"
// affordance. 28 px matches the Table add-row height so both views
// sit at the same visual rhythm.
const ADD_ROW_HEIGHT = 28;
const EDGELESS_INTRINSIC_HEIGHT_GUARD = 4;
const GANTT_SEPARATOR_WIDTH = '1px';
const GANTT_SEPARATOR_COLOR = cssVarV2.database.border;

// Axis-wide horizontal canvas. The scroll container stays viewport-sized,
// while this wrapper expands to `LABEL_COLUMN_WIDTH + axisWidth` so the
// header, rows, bars and dependency overlay share the same coordinate
// space and scroll horizontally as a single unit.
const ganttAxisContentStyle = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
});

// The header actions and their fade must paint exactly the header's own
// background, or they show up as a tinted patch next to the buttons.
const GANTT_LABEL_HEADER_BACKGROUND =
  'var(--streamlain-gantt-header-background, var(--streamlain-v2-layer-background-primary))';

const ganttAxisHeaderRowStyle = css({
  position: 'sticky',
  top: 0,
  zIndex: 6,
  display: 'flex',
  height: `${TIME_AXIS_HEADER_HEIGHT}px`,
  background: GANTT_LABEL_HEADER_BACKGROUND,
});

const ganttLabelHeaderCellStyle = css({
  // zIndex 4 sits above the sibling `<dv-gantt-time-axis-header>`
  // (zIndex 2) inside the parent header row. Without the lift, the
  // sticky label header got painted over by the scrolled time-axis
  // tick labels that share the same stacking context. Font metrics
  // (14px, weight 500) match the Table column-text style so the
  // header reads identically across views.
  position: 'sticky',
  left: 0,
  zIndex: 4,
  display: 'flex',
  alignItems: 'stretch',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--streamlain-v2-text-secondary)',
  background: GANTT_LABEL_HEADER_BACKGROUND,
  borderRight: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  borderBottom: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  boxShadow: '6px 0 8px -6px rgba(0, 0, 0, 0.08)',
  boxSizing: 'border-box',
});

// Viewport-sized clipping window inside the sticky label header.
// Horizontal scroll is NOT native here — a signal
// (`labelScrollLeft$`) drives a `translateX` on the inner wrapper
// below, shared with the body's inner wrapper so header + rows are
// always locked together by construction. Keeping horizontal scroll
// off this element also stops the outer gantt scroll container from
// ever receiving a runaway horizontal wheel (the wheel handler on
// the sticky cell consumes and `preventDefault`s the delta).
const ganttLabelHeaderContentStyle = css({
  position: 'relative',
  flex: '1 1 auto',
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
});

// Inner transform wrapper: holds the flex row of title + extra cells
// at its natural `contentLabelWidth`. The `translateX(-scrollLeft)`
// is applied inline at render time from the shared signal.
const ganttLabelHeaderInnerStyle = css({
  display: 'flex',
  alignItems: 'stretch',
  height: '100%',
  // `willChange` hints the compositor so the translate stays cheap
  // during continuous wheel input.
  willChange: 'transform',
});

const labelHeaderTextStyle = css({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// Pseudo spacer in the sticky header that reserves horizontal space
// equal to the row's drag-handle + checkbox slot (~28 px). Without
// it the header icon + text start further left than the row icon +
// text (which are pushed right by those hover-only affordances), so
// header and rows read mis-aligned. Matches the Table left-bar that
// lives outside its first cell for the same reason.
const labelHeaderLeadingSpacerStyle = css({
  flex: '0 0 auto',
  display: 'inline-block',
  width: '28px',
});

// Leading icon slot inside the sticky header's title cell. Matches
// the Table column-type icon styling (padding 2px, border-radius
// 4px, 18px font) so the column-type icon reads identically next to
// its property name across both views.
const labelHeaderIconStyle = css({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  borderRadius: '4px',
  fontSize: '18px',
  color: 'var(--streamlain-v2-text-secondary)',

  '& svg': {
    width: '16px',
    height: '16px',
  },
});

// Title-cell slot inside the sticky header. Owns the resizer handle
// and the header chrome (name + collapse + picker buttons). The
// 10 px left padding matches the row's `labelCellStyle` so the
// spacer + icon land at the same x-coordinate as the row's drag
// handle + checkbox + icon stack below.
const labelHeaderTitleCellStyle = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '0 6px 0 10px',
  height: '100%',
  borderRight: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  boxSizing: 'border-box',

  // Collapsed strip shows only the expand button. Kill the padding,
  // centre via flex, and override the button's `marginLeft: auto`
  // (which in the expanded state pushes it to the right edge).
  '&[data-collapsed=true]': {
    justifyContent: 'center',
    padding: 0,
  },
});

// Header actions are pinned to the visible sticky label section, not
// inside the horizontally translated label-content strip. Column and
// section resizing can make that strip overflow and scroll internally;
// keeping the actions out of it preserves the collapse/properties
// controls even when the title/extra columns are clipped or scrolled.
const labelHeaderActionsStyle = css({
  position: 'absolute',
  top: '50%',
  right: '2px',
  zIndex: 6,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  transform: 'translateY(-50%)',
  padding: '2px',
  borderRadius: '6px',
  background: GANTT_LABEL_HEADER_BACKGROUND,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-18px',
    width: '18px',
    background: `linear-gradient(to right, transparent 0, ${GANTT_LABEL_HEADER_BACKGROUND} 100%)`,
    pointerEvents: 'none',
  },

  '& > button': {
    position: 'relative',
    marginLeft: 0,
    background: GANTT_LABEL_HEADER_BACKGROUND,
  },
});

// One slot per extra column — static 140 px each. Keeps the header
// scrollable horizontally alongside the title (no internal resize
// handle; widening is handled by the picker which decides which
// columns show). Horizontal padding matches the row's 6 px so
// headers land at the same x-coordinate as the row cell contents.
const labelHeaderExtraCellStyle = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: '0 6px',
  height: '100%',
  borderRight: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  boxSizing: 'border-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 'var(--streamlain-font-xs)',
  fontWeight: 600,
  color: 'var(--streamlain-v2-text-secondary)',
  touchAction: 'none',

  '&.dnd-active': {
    opacity: 0.45,
    zIndex: 4,
  },
});

// Title-cell wrapper inside a label row. Hosts the leading stack
// (drag handle → checkbox → icon → title `<dv-data-view-cell-container>`
// → row-ops). The 10 px horizontal padding forms the leading gutter
// the drag handle (`marginLeft: -6px`) sits inside of, and matches
// the header's `labelHeaderTitleCellStyle` so the two rows line up.
const labelCellStyle = css({
  // `position: relative` anchors the trailing row-ops slot (peek /
  // more menu) — see `labelRowOpsSlotStyle` below. Row-ops became
  // an absolute overlay so the title text can flow to the full
  // cell width; without it the inline flex item stole horizontal
  // space from the title column and truncated the label visibly.
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: '0 10px',
  borderRight: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  boxSizing: 'border-box',
  height: '100%',
  overflow: 'hidden',

  '&:last-child': {
    borderRight: 'none',
  },

  // Collapsed state: only the checkbox is visible and the cell is
  // ~32 px wide. Centre the checkbox horizontally so it doesn't butt
  // up against the right border (which reads as "missing padding"
  // even though the expanded-state 10 px gutter is technically there).
  // Mirrors the header's collapsed behaviour so checkbox and the
  // header's expand button sit on the same x-axis.
  '&[data-collapsed=true]': {
    padding: 0,
    justifyContent: 'center',

    // Collapsed strip: only the checkbox is meaningful. Hide every
    // other direct child (drag handle, leading type icon, title
    // renderer, row-ops) so the 32 px column isn't fighting five
    // flex items for space — `justify-content: center` on the cell
    // then lands the checkbox dead centre.
    '& > *:not([data-role="checkbox-slot"])': {
      display: 'none',
    },
    // Kill the checkbox slot's trailing margin so it centres
    // precisely (otherwise the 6 px trailing gutter from the
    // expanded-state layout biases the midpoint to the left).
    '& > [data-role="checkbox-slot"]': {
      marginRight: 0,
    },
  },
});

// Extra-column cell wrapper inside a label row. Carries only the
// column border + height — horizontal padding lives on the inner
// `<dv-data-view-cell-container>` (same 6 px the table view paints
// on its own cells) so the gantt label row and the table row show
// identical content insets. Without this the row's 10 px added
// visually on top of the cell's 6 px, leaving the gantt content
// indented 10 px further than the table.
const labelCellExtraStyle = css({
  display: 'flex',
  alignItems: 'center',
  borderRight: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  boxSizing: 'border-box',
  height: '100%',
  overflow: 'hidden',

  '&:last-child': {
    borderRight: 'none',
  },
});

// Flat icon button that toggles the column between default (220 px) and
// min (32 px). Sits inside the sticky header cell; when the column is
// collapsed the text is hidden and the button centres itself.
const labelCollapseButtonStyle = css({
  flex: '0 0 auto',
  marginLeft: 'auto',
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  borderRadius: '4px',
  padding: '2px',
  width: '20px',
  height: '20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--streamlain-v2-text-secondary)',

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
    color: 'var(--streamlain-v2-text-primary)',
  },

  '& svg': {
    width: '16px',
    height: '16px',
  },
});

const ganttAxisHeaderFillStyle = css({
  flex: '1 1 auto',
  position: 'relative',
});

// 6px hit area on the right edge of the title column header. Shows a
// subtle accent on hover so users discover it; pointer capture on the
// handle itself means the drag keeps tracking even if the cursor
// leaves the element while resizing.
const labelColumnResizerStyle = css({
  position: 'absolute',
  top: 0,
  right: '-3px',
  width: '6px',
  height: '100%',
  cursor: 'col-resize',
  touchAction: 'none',
  userSelect: 'none',
  zIndex: 1,

  '&:hover, &:active': {
    background:
      'linear-gradient(to right, transparent 0, var(--streamlain-v2-brand-primary) 50%, transparent 100%)',
  },
});

// Split-pane divider between the sticky label section and the
// timeline. Visual language follows the MS Project / Smartsheet /
// Syncfusion convention: the sticky label's existing `border-right`
// (1 px) is the persistent divider — this rule is a transparent
// hit-zone that overlays the seam and highlights the border on
// hover. Keeping the visible line at 1 px (and lighting it up to
// `primary-color` on hover) is what distinguishes the splitter from
// per-column handles, which only appear on header-row hover and
// never show as a full-height bar. No grip glyph — the always-on
// 1 px line is sufficient; dots would fight for visual weight
// against the column-right borders directly beside them.
const sectionResizerStyle = css({
  position: 'absolute',
  top: 0,
  // Hit zone sits FULLY OUTSIDE the sticky label's right edge
  // ([parent_right, parent_right + 8]) so per-column handles inside
  // the last cell ([parent_right - 6, parent_right]) stay reachable.
  // Zero pixel overlap is the non-negotiable — otherwise the last
  // extra column becomes unresizable.
  right: '-8px',
  width: '8px',
  height: '100%',
  cursor: 'col-resize',
  touchAction: 'none',
  userSelect: 'none',
  zIndex: 5,

  // Highlight strip sits at the handle's INNER edge (left: 0), which
  // is exactly at the sticky label's right edge — kissing the
  // existing 1 px `border-right` on the label. Default transparent
  // (the label's border carries the idle visual); hover widens to
  // 2 px in primary so the divider reads as "grabbed".
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '2px',
    background: 'transparent',
    transition: 'background 120ms ease',
    pointerEvents: 'none',
  },

  '&:hover::before, &:active::before': {
    background: 'var(--streamlain-v2-brand-primary)',
  },
});

// Column-width handle on each extra column header. Kept fully INSIDE
// the cell (`right: 0`, no negative offset) so it never overlaps the
// section-resize handle that projects past the sticky label's right
// edge — the two handles occupy different horizontal zones.
const extraColumnResizerStyle = css({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '6px',
  height: '100%',
  cursor: 'col-resize',
  touchAction: 'none',
  userSelect: 'none',
  zIndex: 1,

  '&:hover, &:active': {
    background:
      'linear-gradient(to right, transparent 0, var(--streamlain-v2-brand-primary) 50%, transparent 100%)',
  },
});

const ganttRowsRowStyle = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
});

const ganttLabelColumnStyle = css({
  // zIndex 3 sits above the bars area (z auto) and the today
  // indicator overlay (z 10 — scoped to its own stacking context
  // inside the bars area) so the sticky column reads as a true
  // overlay during horizontal scroll.
  position: 'sticky',
  left: 0,
  zIndex: 3,
  background: 'var(--streamlain-v2-layer-background-primary)',
  borderRight: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  // Casts a subtle shadow onto the scroll surface so the sticky
  // column reads as an overlay sitting above the bars instead of a
  // pane sharing the same plane.
  boxShadow: '6px 0 8px -6px rgba(0, 0, 0, 0.08)',
  boxSizing: 'border-box',
});

// Viewport-sized clipping window for the sticky label column body.
// Same contract as the header content style above: no native scroll,
// a translateX-driven inner wrapper mirrors the signal shared with
// the header so rows and column headers never drift apart.
const ganttLabelColumnContentStyle = css({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
});

// Inner transform wrapper for the body rows. Sized to the natural
// `contentLabelWidth` so absolute-positioned rows (one per row index,
// `top: index * ROW_HEIGHT`) can extend their full width; clipped by
// the outer content-style window.
const ganttLabelColumnInnerStyle = css({
  position: 'relative',
  height: '100%',
  willChange: 'transform',
});

const ganttLabelRowStyle = css({
  position: 'absolute',
  left: 0,
  display: 'flex',
  alignItems: 'stretch',
  borderBottom: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  cursor: 'pointer',
  userSelect: 'none',
  // Soft glide for the live reorder preview — the transform shift is
  // recomputed on every pointermove.
  transition: 'transform 120ms ease-out',
  willChange: 'transform',

  // No background change on row hover — matches Table where hover
  // only reveals the drag handle + checkbox + ops (see the rules
  // further down) and leaves the row fill alone. The earlier subtle
  // wash made Gantt rows feel "heavier" than Table rows on hover.

  // Selected row adopts Table's selection tint (4 % primary color)
  // so selected rows read identically across views.
  '&&&[data-selected]': {
    background: 'var(--streamlain-v2-brand-primaryTint)',
  },

  // Row being dragged for reorder fades out in place so the ghost at
  // the cursor reads as the "live" row.
  '&&&[data-dragging]': {
    opacity: 0.35,
  },

  // Match Table's shift-active cursor (`cell`) so the select gesture
  // reads identically across views. Descendant `*` selector is needed
  // because the cells embed rich-text (cursor: text) which otherwise
  // wins over the row-level rule and leaves the cursor visible only
  // in the narrow band below the text baseline — same fix Table
  // uses in row.ts.
  '[data-shift-active] &, [data-shift-active] & *': {
    cursor: 'cell',
  },

  // Hover-reveal affordances. Matches the Table `.show-on-hover-row`
  // pattern so drag handle, checkbox and row-ops only materialise
  // when the user has the row under the cursor (or the row carries a
  // selected checkbox, which stays visible via its own `.selected`
  // opacity rule inside the shared element).
  '& .show-on-hover-row': {
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 120ms ease-out',
  },
  '&:hover .show-on-hover-row': {
    visibility: 'visible',
    opacity: 1,
  },
  // Shared checkbox defaults to opacity:0; row-hover reveals it in
  // lockstep with the other hover-only affordances.
  '&:hover .row-select-checkbox': {
    opacity: 1,
  },
});

// Slot for the shared `dv-row-drag-handle` at the very left edge of
// the label row. The handle itself owns a `cursor: grab` rule; this
// slot only reserves a hair of horizontal space so the handle sits
// tight to the cell border without eating into the title text.
const labelDragHandleSlotStyle = css({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: '-6px',
  marginRight: '4px',
});

// Slot around the shared `dv-row-select-checkbox` inside the title
// cell. Keeps the checkbox a fixed-width leading gutter so the title
// text doesn't jump when the checkbox's opacity flips on hover /
// selection. Pointerdown is stopped upstream so clicking the checkbox
// never accidentally starts a row-reorder drag.
const labelCheckboxSlotStyle = css({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  marginRight: '6px',
});

// Leading-slot type icon — wraps the per-row `iconColumn` value in
// a rounded, tinted box so the Gantt label row matches the Table
// title cell when the user switches between views.
const labelTypeIconStyle = css({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  borderRadius: '4px',
  marginRight: '8px',
  color: 'var(--streamlain-v2-icon-primary)',
  backgroundColor: 'var(--streamlain-v2-layer-background-secondary)',

  '& svg': {
    width: '16px',
    height: '16px',
  },
});

// Trailing slot for the shared `<dv-row-ops>` (peek + more menu).
// Absolute overlay pinned to the title cell's right edge so the
// title text can flow the full cell width underneath. Previously
// inline at the tail of the flex row, which ate 40-48 px of title
// column every row. The `show-on-hover-row` class (wired at
// render time) keeps the slot invisible until the row is hovered.
// Parent cell is `position: relative` (see `labelCellStyle`).
// Background is a small pill of the row's surface so icons stay
// readable when they hover over the tinted cell area.
const labelRowOpsSlotStyle = css({
  // Absolute-positioned overlay; each <dv-row-op> inside carries its
  // own white bg + button-shadow. Wrapping the cluster in another
  // filled chip was a double chrome that Table view does not paint —
  // the two buttons should read as individual floating pills over
  // the title text, not as a bordered widget group. `right: 0` matches
  // Table's layout: the 8 px gap from the cell boundary lives on
  // `.dv-row-op:last-child { margin-right: 8px }` inside the shared
  // component, not on the outer slot. Adding extra `right: 6px` here
  // stacked on top of that 8 px, pushing the cluster too far inward
  // compared to the same component in the table view.
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  // Lift over the inline title content so hovered row-ops sit on
  // top of the last few characters when the title fills the cell.
  zIndex: 1,
});

// "+ New Record" affordance below the rows. Spans the full gantt
// canvas width (label column + axis) so the bottom separator sits
// flush with every column and the row can be clicked anywhere to
// append a new record — matches Table's full-width add-row.
const ganttAddRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: `${ADD_ROW_HEIGHT}px`,
  padding: '4px 0',
  cursor: 'pointer',
  userSelect: 'none',
  borderBottom: `${GANTT_SEPARATOR_WIDTH} solid ${GANTT_SEPARATOR_COLOR}`,
  boxSizing: 'border-box',
  transition: 'background-color 150ms ease-out',

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
  },
});

// Inner label for the "+ New Record" row — sticky to the viewport's
// left edge so the plus icon + text stay visible while the user
// scrolls the time axis horizontally. Matches Table's add-row button
// which uses `position: sticky; left: …` for the same reason.
const ganttAddRowStickyButtonStyle = css({
  position: 'sticky',
  left: '8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '12px',
  lineHeight: '20px',
  color: 'var(--streamlain-v2-text-secondary)',

  '& svg': {
    width: '16px',
    height: '16px',
    fill: 'var(--streamlain-v2-icon-primary)',
  },
});

// Horizontal drop indicator rendered inside the sticky label column
// during a reorder drag. Spans the full column width so it reads as a
// clear gap marker between rows.
const reorderIndicatorStyle = css({
  position: 'absolute',
  left: 0,
  height: '2px',
  background: 'var(--streamlain-v2-brand-primary)',
  borderRadius: '1px',
  transform: 'translateY(-1px)',
  pointerEvents: 'none',
  zIndex: 5,
  boxShadow:
    '0 0 0 2px color-mix(in srgb, var(--streamlain-v2-brand-primary) 20%, transparent)',
});

const ganttLabelTextStyle = css({
  flex: '1 1 auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 'var(--data-view-cell-text-size)',
  lineHeight: 'var(--data-view-cell-text-line-height)',
  color: 'var(--streamlain-v2-text-primary)',
});

// Slot that hosts the title property's own cell renderer (rich-text
// editor). Keeps the flex-layout behaviour of `ganttLabelTextStyle`
// (grow + ellipsis when collapsed) while letting the rich-text fill
// its full width for inline-edit. No own text color — the renderer
// supplies its own.
// Minimal flex slot around the shared `<dv-data-view-cell-container>`.
// Sizing, padding, overflow, ellipsis variables and min-width guards
// all live on the cell-container itself so the gantt's label column
// and the table view produce byte-for-byte identical cell chrome —
// this wrapper only contributes the label-row's flex geometry.
const ganttLabelCellSlotStyle = css({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  fontSize: 'var(--data-view-cell-text-size)',
  lineHeight: 'var(--data-view-cell-text-line-height)',

  '& > dv-data-view-cell-container': {
    flex: '1 1 auto',
    minWidth: 0,
  },
});

const ganttBarsAreaStyle = css({
  flex: '1 1 auto',
  position: 'relative',
  // `isolation: isolate` establishes a new stacking context so the
  // today indicator's z-index stays LOCAL to the bars area. Without it
  // the indicator escapes to the parent stacking context and paints
  // over the sticky label column (z:3 there < z:10 here).
  isolation: 'isolate',
  // Clip the today line horizontally so it disappears cleanly when it
  // scrolls past the bars-area edge. Vertical overflow stays visible so
  // tooltips and ghost clones can still render across the boundary.
  overflow: 'hidden',
});

const ganttBarsRowSeparatorLayerStyle = css({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
});

const ganttBarsRowSeparatorStyle = css({
  position: 'absolute',
  left: 0,
  right: 0,
  height: GANTT_SEPARATOR_WIDTH,
  background: GANTT_SEPARATOR_COLOR,
  pointerEvents: 'none',
});

const ganttEmptyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '32px',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  textAlign: 'center',
});

const ganttEmptyTitleStyle = css({
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--streamlain-v2-text-primary)',
});

const ganttEmptyHintStyle = css({
  fontSize: '13px',
  color: 'var(--streamlain-v2-text-secondary)',
  maxWidth: '320px',
});

const wizardOpenerStyle = css({
  appearance: 'none',
  border: 'none',
  padding: '6px 14px',
  borderRadius: '4px',
  fontSize: '13px',
  cursor: 'pointer',
  marginTop: '8px',
  background: 'var(--streamlain-v2-button-primary)',
  color: 'var(--streamlain-v2-button-pureWhiteText)',
});

// Floating "items live off the visible range" affordance. One on each
// side of the gantt body, shown only when `offscreen.{left|right} > 0`.
// Positioned absolutely in `ganttBodyStyle` (position: relative), so
// they stay pinned to the visible gantt edges regardless of horizontal
// scroll. `top` is set inline to the centre of the visible row body
// (see {@link offscreenIndicatorTop}). `left: 0` intentionally sits over the sticky label column's
// left edge — per product direction, the indicator reads "before the
// visible bars" and the sticky column is where the user starts.
const offscreenIndicatorBaseStyle = css({
  position: 'absolute',
  transform: 'translateY(-50%)',
  zIndex: 6,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 10px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  appearance: 'none',
  boxShadow: 'var(--streamlain-v2-shadow-level2)',
  backdropFilter: 'blur(8px)',
  transition: 'transform 140ms ease-out, background 140ms ease-out',
  userSelect: 'none',
  background:
    'color-mix(in srgb, var(--streamlain-v2-layer-background-primary) 85%, transparent)',
  color: 'var(--streamlain-v2-text-primary)',

  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
  },
});

// Left/right variants carry only side-specific tweaks. The actual
// `left` / `right` distance is set at call time via inline style so
// the left indicator can start past the sticky label column (see
// {@link renderOffscreenIndicator} for the architectural invariant).
const offscreenIndicatorLeftStyle = css(offscreenIndicatorBaseStyle, {});

const offscreenIndicatorRightStyle = css(offscreenIndicatorBaseStyle, {});

const offscreenIndicatorArrowStyle = css({
  fontSize: '16px',
  lineHeight: 1,
  fontWeight: 700,
});

const offscreenIndicatorCountStyle = css({
  minWidth: '14px',
  textAlign: 'center',
});

// Header banner shown when a gantt role column was deleted in the table
// view.
const ganttMissingColumnsBannerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '8px 14px',
  margin: '0',
  borderBottom: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background:
    'color-mix(in srgb, var(--streamlain-v2-status-warning, #f59e0b) 12%, var(--streamlain-v2-layer-background-primary))',
  color: 'var(--streamlain-v2-text-primary)',
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 500,
});

const ganttMissingColumnsBannerTextStyle = css({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const ganttMissingColumnsBannerButtonStyle = css({
  flex: '0 0 auto',
  padding: '4px 10px',
  borderRadius: '6px',
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  background: 'var(--streamlain-v2-layer-background-primary)',
  color: 'var(--streamlain-v2-text-primary)',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  appearance: 'none',
  '&:hover': {
    background: 'var(--streamlain-v2-layer-background-hoverOverlay)',
  },
});

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-view-ui': GanttViewUI;
  }
}
